import React, { useState, useEffect } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import {
  Store, RefreshCw, Upload, Download, Trash2, Eye, Check, X,
  AlertCircle, Loader2, Search, Filter, ExternalLink, 
  CloudOff, Cloud, Package, ShoppingCart, Clock, CheckCircle,
  XCircle, AlertTriangle, ArrowUpRight, Settings, Shield, Lock, Image,
  Plus, MoreVertical, Globe, BarChart2, Folder, Code, Activity, Link2
} from 'lucide-react';

const AdminShopifySync = () => {
  const { toast } = useToast();
  const { apiCall } = useAuth();
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Connection status
  const [connectionStatus, setConnectionStatus] = useState(null);
  
  // Listings state
  const [listings, setListings] = useState([]);
  const [selectedListings, setSelectedListings] = useState([]);
  const [listingSearch, setListingSearch] = useState('');
  const [syncFilter, setSyncFilter] = useState('all'); // all, synced, pending, failed
  const [sortBy, setSortBy] = useState('updated_at'); // updated_at, created_at, title, price, vendor
  const [sortOrder, setSortOrder] = useState('desc'); // asc, desc
  const [listingPage, setListingPage] = useState(1);
  const [totalListings, setTotalListings] = useState(0);
  
  // Sync history
  const [syncHistory, setSyncHistory] = useState([]);
  
  // Bulk action progress
  const [bulkProgress, setBulkProgress] = useState(null);

  // Inventory bulk sync state
  const [inventorySyncing, setInventorySyncing] = useState(false);
  const [invSyncResult, setInvSyncResult] = useState(null);

  // Content Protection state
  const [contentProtection, setContentProtection] = useState({
    enabled: false,
    loading: false,
    syncing: false,
    totalStores: 0,
    protectedStores: 0,
    lastSyncedAt: null
  });

  // Sync Settings state
  const [syncSettings, setSyncSettings] = useState(null);
  const [syncSettingsLoading, setSyncSettingsLoading] = useState(false);
  const [scheduleStatus, setScheduleStatus] = useState(null);
  
  // Store management state
  const [showStoreModal, setShowStoreModal] = useState(false);
  const [newStoreUrl, setNewStoreUrl] = useState('');
  const [accessToken, setAccessToken] = useState('');
  const [webhookSecret, setWebhookSecret] = useState('');
  const [storeConnecting, setStoreConnecting] = useState(false);
  const [installedStores, setInstalledStores] = useState([]);
  const [switchingStore, setSwitchingStore] = useState(false);

  // Remap Shopify ID state
  const [showRemapModal, setShowRemapModal] = useState(false);
  const [remapListings, setRemapListings] = useState([]);
  const [remapLoading, setRemapLoading] = useState(false);
  const [remapInputs, setRemapInputs] = useState({});  // { listing_id: "shopify_url_or_id" }
  const [remapSaving, setRemapSaving] = useState(false);

  // Fresh Push All state
  const [showFreshPushModal, setShowFreshPushModal] = useState(false);
  const [freshPushConfirmText, setFreshPushConfirmText] = useState('');
  const [freshPushRunning, setFreshPushRunning] = useState(false);

  // Injection Status state
  const [injectionStatus, setInjectionStatus] = useState(null);
  const [injectionLoading, setInjectionLoading] = useState(false);

  // Inventory Audit state
  const [auditData, setAuditData] = useState(null);
  const [auditLoading, setAuditLoading] = useState(false);
  const [showAuditModal, setShowAuditModal] = useState(false);

  // Liquid Audit state
  const [liquidAuditReport, setLiquidAuditReport] = useState(null);
  const [liquidAuditLoading, setLiquidAuditLoading] = useState(false);
  const [showLiquidAuditModal, setShowLiquidAuditModal] = useState(false);
  const [patchingVariantJson, setPatchingVariantJson] = useState(false);

  // Render Chain Audit state
  const [renderChainReport, setRenderChainReport] = useState(null);
  const [renderChainLoading, setRenderChainLoading] = useState(false);
  const [showRenderChainModal, setShowRenderChainModal] = useState(false);

  // File Inspector state
  const [inspectedFile, setInspectedFile] = useState(null);
  const [inspectLoading, setInspectLoading] = useState(false);
  const [inspectKey, setInspectKey] = useState('');

  useEffect(() => {
    fetchData();
    fetchContentProtectionStatus();
    fetchSyncSettings();
    fetchInstalledStores();
  }, []);

  useEffect(() => {
    if (activeTab === 'listings') {
      fetchListings();
    }
    if (activeTab === 'settings') {
      fetchScheduleStatus();
    }
  }, [listingSearch, syncFilter, sortBy, sortOrder, listingPage, activeTab]);

  // Fetch content protection status
  const fetchContentProtectionStatus = async () => {
    try {
      const response = await apiCall('/api/shopify/content-protection/status');
      if (response) {
        setContentProtection(prev => ({
          ...prev,
          enabled: response.is_protected || false,
          loading: false
        }));
      }
    } catch (error) {
      console.error('Error fetching content protection status:', error);
      setContentProtection(prev => ({ ...prev, loading: false }));
    }
  };

  // Toggle content protection for all stores
  const handleToggleContentProtection = async (enabled) => {
    setContentProtection(prev => ({ ...prev, loading: true }));
    
    try {
      if (enabled) {
        // Enable content protection - sync to all stores
        const response = await apiCall('/api/shopify/content-protection/admin/sync-all', {
          method: 'POST'
        });
        
        if (response?.success) {
          setContentProtection(prev => ({
            ...prev,
            enabled: true,
            loading: false,
            totalStores: response.total_stores || 0,
            protectedStores: response.protected || 0,
            lastSyncedAt: new Date().toISOString()
          }));
          
          toast({
            title: 'Content Protection Enabled',
            description: `Protected ${response.protected} of ${response.total_stores} stores`
          });
        } else {
          throw new Error('Failed to enable content protection');
        }
      } else {
        // Disable content protection
        const response = await apiCall('/api/shopify/content-protection/disable', {
          method: 'POST'
        });
        
        if (response?.success) {
          setContentProtection(prev => ({
            ...prev,
            enabled: false,
            loading: false
          }));
          
          toast({
            title: 'Content Protection Disabled',
            description: 'Image and text protection has been removed from stores'
          });
        } else {
          throw new Error('Failed to disable content protection');
        }
      }
    } catch (error) {
      console.error('Content protection error:', error);
      setContentProtection(prev => ({ ...prev, loading: false }));
      toast({
        title: 'Error',
        description: error.message || 'Failed to update content protection',
        variant: 'destructive'
      });
    }
  };

  // Fetch sync settings
  const fetchSyncSettings = async () => {
    try {
      const response = await apiCall('/api/shopify-sync/settings');
      setSyncSettings(response);
    } catch (error) {
      console.error('Error fetching sync settings:', error);
      // Set defaults if endpoint fails
      setSyncSettings({
        auto_sync_enabled: false,
        auto_sync_on_approval: false,
        auto_sync_inventory: true,
        auto_sync_price: true,
        sync_mode: 'manual'
      });
    }
  };

  // Fetch schedule status
  const fetchScheduleStatus = async () => {
    try {
      const response = await apiCall('/api/shopify-sync/schedule-status');
      setScheduleStatus(response);
    } catch (error) {
      console.error('Error fetching schedule status:', error);
    }
  };

  // Trigger manual scheduled sync
  const handleTriggerScheduledSync = async () => {
    if (!window.confirm('Run scheduled sync now? This will sync pending listings and update synced ones.')) return;
    
    setSyncing(true);
    try {
      const response = await apiCall('/api/shopify-sync/trigger-scheduled-sync', {
        method: 'POST'
      });
      
      if (response?.success) {
        toast({
          title: 'Sync Triggered',
          description: response.message
        });
        fetchData();
        fetchScheduleStatus();
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to trigger sync',
        variant: 'destructive'
      });
    } finally {
      setSyncing(false);
    }
  };

  // Update sync settings
  const handleUpdateSyncSettings = async (updates) => {
    setSyncSettingsLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(updates).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.append(key, value.toString());
        }
      });
      
      const response = await apiCall(`/api/shopify-sync/settings?${params.toString()}`, {
        method: 'PUT'
      });
      
      if (response?.success) {
        setSyncSettings(prev => ({ ...prev, ...updates }));
        toast({
          title: 'Settings Updated',
          description: 'Shopify sync settings have been saved'
        });
        // Refresh schedule status after settings change
        fetchScheduleStatus();
      }
    } catch (error) {
      console.error('Error updating sync settings:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to update sync settings',
        variant: 'destructive'
      });
    } finally {
      setSyncSettingsLoading(false);
    }
  };

  // Store management handlers
  const fetchInstalledStores = async () => {
    try {
      const response = await apiCall('/api/shopify-app/installed-stores');
      if (response?.stores) {
        setInstalledStores(response.stores);
      }
    } catch (error) {
      console.error('Error fetching installed stores:', error);
    }
  };

  const handleSwitchStore = async (shop) => {
    if (!shop) return;
    
    setSwitchingStore(true);
    try {
      const response = await apiCall(`/api/shopify-app/set-active-store?shop=${encodeURIComponent(shop)}`, {
        method: 'POST'
      });
      
      if (response?.success) {
        toast({
          title: 'Store Switched',
          description: `Now syncing with ${shop}`
        });
        // Refresh all data
        fetchData();
        fetchInstalledStores();
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to switch store',
        variant: 'destructive'
      });
    } finally {
      setSwitchingStore(false);
    }
  };

  const handleConnectNewStore = async () => {
    if (newStoreUrl) {
      setStoreConnecting(true);
      // Use the OAuth flow to install the app on a new store
      const cleanUrl = newStoreUrl.replace('.myshopify.com', '').trim();
      window.location.href = `${process.env.REACT_APP_BACKEND_URL}/api/shopify-app/install?shop=${cleanUrl}.myshopify.com`;
    } else {
      setShowStoreModal(true);
    }
  };

  const handleConnectWithToken = async () => {
    if (!newStoreUrl || !accessToken) return;
    
    setStoreConnecting(true);
    try {
      const cleanUrl = newStoreUrl.replace('.myshopify.com', '').trim();
      const params = new URLSearchParams({
        shop_url: cleanUrl,
        access_token: accessToken,
      });
      if (webhookSecret) params.append('shared_secret', webhookSecret);
      const response = await apiCall(
        `/api/shopify-app/connect-with-token?${params.toString()}`,
        { method: 'POST' }
      );
      
      if (response?.success) {
        toast({
          title: 'Store Connected',
          description: `Successfully connected to ${response.shop_name || response.shop}`
        });
        setShowStoreModal(false);
        setNewStoreUrl('');
        setAccessToken('');
        setWebhookSecret('');
        fetchData();
        fetchInstalledStores();
      }
    } catch (error) {
      toast({
        title: 'Connection Failed',
        description: error.message || 'Failed to connect to Shopify store',
        variant: 'destructive'
      });
    } finally {
      setStoreConnecting(false);
    }
  };

  const handleDisconnectStore = async () => {
    if (!window.confirm('Are you sure you want to disconnect this Shopify store? This will stop all product syncing.')) {
      return;
    }
    
    try {
      const response = await apiCall('/api/shopify-sync/disconnect', { method: 'POST' });
      
      if (response?.success) {
        toast({
          title: 'Store Disconnected',
          description: 'Shopify store has been disconnected successfully'
        });
        fetchData();
        fetchInstalledStores();
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to disconnect store',
        variant: 'destructive'
      });
    }
  };

  const handleReinstallScript = async () => {
    try {
      const response = await apiCall('/api/shopify-theme/install-script', { method: 'POST' });
      if (response?.success) {
        toast({ title: 'Script Reinstalled', description: 'Storefront script updated successfully. Refresh your store to see changes.' });
      } else {
        toast({ title: 'Reinstall Failed', description: response?.message || 'Could not reinstall script', variant: 'destructive' });
      }
    } catch (error) {
      toast({ title: 'Reinstall Error', description: error.message || 'Failed to reinstall script', variant: 'destructive' });
    }
  };

  const handleInjectToTheme = async () => {
    try {
      const response = await apiCall('/api/shopify-theme/install-script-theme', { method: 'POST' });
      if (response?.success) {
        toast({
          title: 'Theme Injection Successful',
          description: `Script injected into ${response.theme_name} theme.liquid. This is more reliable than ScriptTag for OS 2.0 themes.`
        });
      } else {
        toast({ title: 'Injection Failed', description: response?.detail || 'Could not inject script', variant: 'destructive' });
      }
    } catch (error) {
      toast({ title: 'Injection Error', description: error.message || 'Failed to inject script', variant: 'destructive' });
    }
  };

  const handleRemoveFromTheme = async () => {
    if (!window.confirm('Remove the storefront script from theme.liquid?\n\nThis removes the legacy theme.liquid injection. The App Embed will remain active as the single script source.\n\nProceed?')) return;
    try {
      const response = await apiCall('/api/shopify-theme/uninstall-script-theme', { method: 'DELETE' });
      if (response?.success) {
        toast({
          title: 'Removed from Theme',
          description: 'Legacy theme.liquid script injection removed. App Embed is now the sole source — no more duplicate script executions.'
        });
        fetchInjectionStatus();
      } else {
        toast({ title: 'Nothing to Remove', description: response?.message || 'No Aniyor script block found in theme.liquid.' });
      }
    } catch (error) {
      toast({ title: 'Removal Error', description: error.message || 'Failed to remove script from theme', variant: 'destructive' });
    }
  };

  const handleCheckScriptStatus = async () => {
    try {
      const response = await apiCall('/api/shopify-theme/script-status');
      const method = response?.installation_method;
      const methodLabel = method === 'app_embed' ? 'App Embed' : method === 'theme_liquid' ? 'Theme.liquid' : method === 'script_tag' ? 'ScriptTag' : 'None';
      
      if (method === 'none') {
        toast({
          title: 'Script Not Installed',
          description: 'No script found (checked ScriptTags, theme.liquid, and App Embed). Click "Inject into Theme (OS 2.0)" to install.',
          variant: 'destructive',
          duration: 10000
        });
      } else if (response?.issues?.length > 0) {
        toast({
          title: `Script Status: ${methodLabel}`,
          description: `${response.issues[0]}`,
          duration: 10000
        });
      } else {
        const themeInfo = response?.active_theme?.name ? ` | Theme: ${response.active_theme.name}` : '';
        const embedInfo = response?.app_embed?.found ? ` | App Embed: Active (${response.app_embed.blocks?.length || 0} block(s))` : '';
        toast({
          title: 'Script Status OK',
          description: `Method: ${methodLabel} | Store: ${response.shop_url}${themeInfo}${embedInfo} | Heartbeat: ${response.heartbeat ? 'Active' : 'Pending'}`
        });
      }
    } catch (error) {
      toast({ title: 'Status Check Error', description: error.message, variant: 'destructive' });
    }
  };


  const handleLiquidAudit = async () => {
    setLiquidAuditLoading(true);
    setShowLiquidAuditModal(true);
    try {
      const response = await apiCall('/api/shopify-theme/liquid-audit');
      setLiquidAuditReport(response);
    } catch (error) {
      toast({ title: 'Liquid Audit Error', description: error.message, variant: 'destructive' });
      setShowLiquidAuditModal(false);
    } finally {
      setLiquidAuditLoading(false);
    }
  };

  const handlePatchVariantJson = async () => {
    if (!window.confirm('This will patch your active Shopify theme section to inject the missing variant-json and main-product-options JSON blocks.\n\nThe patch is additive — it inserts Liquid code before </form> in the main product section. No existing code is removed.\n\nProceed?')) return;
    setPatchingVariantJson(true);
    try {
      const response = await apiCall('/api/shopify-theme/patch-variant-json-blocks', { method: 'POST' });
      if (response?.success) {
        toast({
          title: response.patched ? 'Patch Applied' : 'Already Patched',
          description: response.message || `Section ${response.section_key} updated.`,
          duration: 8000
        });
        if (liquidAuditReport) handleLiquidAudit();
      } else {
        toast({ title: 'Patch Failed', description: response?.detail || 'Unknown error', variant: 'destructive' });
      }
    } catch (error) {
      toast({ title: 'Patch Error', description: error.message, variant: 'destructive' });
    } finally {
      setPatchingVariantJson(false);
    }
  };



  const handleRenderChainAudit = async () => {
    setRenderChainLoading(true);
    setShowRenderChainModal(true);
    try {
      const response = await apiCall('/api/shopify-theme/render-chain-audit');
      setRenderChainReport(response);
    } catch (error) {
      toast({ title: 'Render Chain Audit Error', description: error.message, variant: 'destructive' });
      setShowRenderChainModal(false);
    } finally {
      setRenderChainLoading(false);
    }
  };


  const handleInspectFile = async (key) => {
    const fileKey = key || inspectKey;
    if (!fileKey) return;
    setInspectLoading(true);
    setInspectedFile(null);
    try {
      const response = await apiCall(`/api/shopify-theme/inspect-file?key=${encodeURIComponent(fileKey)}`);
      setInspectedFile(response);
    } catch (error) {
      toast({ title: 'Inspect Error', description: error.message, variant: 'destructive' });
    } finally {
      setInspectLoading(false);
    }
  };



  const handleTestConnection = async () => {
    try {
      const response = await apiCall('/api/shopify-sync/test-connection');
      if (response?.connected) {
        toast({
          title: 'Connection Successful',
          description: `Connected to ${response.shop_name}`
        });
      } else {
        toast({
          title: 'Connection Failed',
          description: response?.error || 'Could not connect to Shopify',
          variant: 'destructive'
        });
      }
    } catch (error) {
      toast({
        title: 'Connection Error',
        description: error.message || 'Failed to test connection',
        variant: 'destructive'
      });
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      // Get sync status
      const statusRes = await apiCall('/api/shopify-sync/status');
      setConnectionStatus(statusRes);
      
      // Get sync history
      const historyRes = await apiCall('/api/shopify-sync/history');
      if (historyRes?.history) {
        setSyncHistory(historyRes.history);
      }
      
      await fetchListings();
    } catch (error) {
      console.error('Error fetching data:', error);
      toast({ title: 'Error', description: 'Failed to load Shopify sync data', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const fetchListings = async () => {
    try {
      let url = `/api/listings/?status=active`;
      if (listingSearch) url += `&search=${encodeURIComponent(listingSearch)}`;
      
      const response = await apiCall(url);
      if (response) {
        // API returns array directly, not object with listings property
        let allListings = Array.isArray(response) ? response : (response.listings || []);
        let filteredListings = allListings;
        
        // Apply sync filter — per-store aware
        if (syncFilter === 'synced') {
          filteredListings = filteredListings.filter(l => {
            const shopUrl = connectionStatus?.shop_url;
            if (shopUrl) {
              const key = shopUrl.replace(/\./g, '__');
              const entry = l.shopify_stores?.[key];
              return entry?.product_id && entry?.sync_status === 'synced';
            }
            return l.shopify_product_id;
          });
        } else if (syncFilter === 'pending') {
          filteredListings = filteredListings.filter(l => {
            const shopUrl = connectionStatus?.shop_url;
            if (shopUrl) {
              const key = shopUrl.replace(/\./g, '__');
              const entry = l.shopify_stores?.[key];
              // Pending = no entry at all, or no product_id and not a failure
              if (!entry) return true;
              if (entry.sync_status === 'synced') return false;
              if (entry.sync_status?.includes('failed')) return false;
              return !entry.product_id;
            }
            return !l.shopify_product_id;
          });
        } else if (syncFilter === 'failed') {
          filteredListings = filteredListings.filter(l => {
            const shopUrl = connectionStatus?.shop_url;
            if (shopUrl) {
              const key = shopUrl.replace(/\./g, '__');
              const entry = l.shopify_stores?.[key];
              return entry?.sync_status?.includes('failed');
            }
            return l.shopify_sync_status?.includes('failed');
          });
        }
        
        // Apply sorting
        filteredListings.sort((a, b) => {
          let aVal, bVal;
          
          switch (sortBy) {
            case 'title':
              aVal = (a.title || '').toLowerCase();
              bVal = (b.title || '').toLowerCase();
              break;
            case 'price':
              aVal = parseFloat(a.bumped_price || a.aniyor_listing_price || a.price || a.base_price || 0);
              bVal = parseFloat(b.bumped_price || b.aniyor_listing_price || b.price || b.base_price || 0);
              break;
            case 'vendor':
              aVal = (a.vendor_name || a.vendor?.name || '').toLowerCase();
              bVal = (b.vendor_name || b.vendor?.name || '').toLowerCase();
              break;
            case 'created_at':
              aVal = new Date(a.created_at || 0).getTime();
              bVal = new Date(b.created_at || 0).getTime();
              break;
            case 'shopify_synced_at':
              aVal = new Date(a.shopify_synced_at || 0).getTime();
              bVal = new Date(b.shopify_synced_at || 0).getTime();
              break;
            case 'updated_at':
            default:
              aVal = new Date(a.updated_at || 0).getTime();
              bVal = new Date(b.updated_at || 0).getTime();
              break;
          }
          
          if (sortOrder === 'asc') {
            return aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
          } else {
            return aVal < bVal ? 1 : aVal > bVal ? -1 : 0;
          }
        });
        
        // Apply client-side pagination
        const startIndex = (listingPage - 1) * 20;
        const paginatedListings = filteredListings.slice(startIndex, startIndex + 20);
        
        setListings(paginatedListings);
        setTotalListings(filteredListings.length);
      }
    } catch (error) {
      console.error('Error fetching listings:', error);
    }
  };

  const handlePushToShopify = async (listingId) => {
    setSyncing(true);
    try {
      const response = await apiCall(`/api/shopify-sync/push/${listingId}`, { method: 'POST' });
      if (response?.success) {
        toast({ 
          title: 'Success', 
          description: 'Product pushed to Shopify successfully' 
        });
        fetchListings();
        fetchData();
      }
    } catch (error) {
      toast({ 
        title: 'Error', 
        description: error.message || 'Failed to push to Shopify', 
        variant: 'destructive' 
      });
    } finally {
      setSyncing(false);
    }
  };

  const handleUnpublish = async (listingId) => {
    if (!window.confirm('Unpublish this product from Shopify?')) return;
    
    setSyncing(true);
    try {
      const response = await apiCall(`/api/shopify-sync/unpublish/${listingId}`, { method: 'POST' });
      if (response?.success) {
        toast({ title: 'Success', description: 'Product unpublished from Shopify' });
        fetchListings();
        fetchData();
      }
    } catch (error) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    } finally {
      setSyncing(false);
    }
  };

  const handleRemove = async (listingId) => {
    if (!window.confirm('Remove this product from Shopify completely? This cannot be undone.')) return;
    
    setSyncing(true);
    try {
      const response = await apiCall(`/api/shopify-sync/remove/${listingId}`, { method: 'DELETE' });
      if (response?.success) {
        toast({ title: 'Success', description: 'Product removed from Shopify' });
        fetchListings();
        fetchData();
      }
    } catch (error) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    } finally {
      setSyncing(false);
    }
  };

  const handleBulkPush = async () => {
    if (selectedListings.length === 0) {
      toast({ title: 'Warning', description: 'Please select listings to push', variant: 'destructive' });
      return;
    }
    
    if (!window.confirm(`Push ${selectedListings.length} products to Shopify?`)) return;
    
    setBulkProgress({ total: selectedListings.length, current: 0, status: 'running', success: 0, failed: 0 });
    
    try {
      const response = await apiCall('/api/shopify-sync/bulk-push', {
        method: 'POST',
        body: JSON.stringify({
          listing_ids: selectedListings,
          action: 'push'
        })
      });
      
      if (response?.task_id) {
        setBulkProgress(prev => ({
          ...prev,
          total: response.total,
          taskId: response.task_id
        }));
        
        toast({
          title: 'Sync Started',
          description: `Background sync started for ${response.total} listings`
        });
        
        // Poll for progress
        const pollInterval = setInterval(async () => {
          try {
            const status = await apiCall(`/api/shopify-sync/bulk-push-status/${response.task_id}`);
            if (status) {
              setBulkProgress(prev => ({
                ...prev,
                current: status.current || 0,
                success: status.success || 0,
                failed: status.failed || 0,
                status: status.status
              }));
              
              if (status.status === 'complete') {
                clearInterval(pollInterval);
                toast({
                  title: 'Bulk Push Complete',
                  description: `${status.success}/${status.total || response.total} synced${status.failed > 0 ? `, ${status.failed} failed` : ''}`
                });
                setSelectedListings([]);
                fetchListings();
                fetchData();
              }
            }
          } catch {
            // Ignore polling errors
          }
        }, 3000);
        
        // Safety: stop polling after 10 minutes
        setTimeout(() => clearInterval(pollInterval), 600000);
      }
    } catch (error) {
      setBulkProgress({ status: 'error', error: error.message });
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  };

  // Re-sync ALL active products to Shopify (no selection needed)
  const handleResyncAll = async () => {
    if (!window.confirm('Re-sync ALL active listings to Shopify?\n\nThis will update pricing, tax settings, and product details for every active listing. It runs in the background — you can check progress here.')) return;
    
    setBulkProgress({ total: 0, current: 0, status: 'running' });
    
    try {
      const response = await apiCall('/api/shopify-sync/bulk-push', {
        method: 'POST',
        body: JSON.stringify({})  // Empty = sync all active
      });
      
      if (response?.task_id) {
        setBulkProgress({
          total: response.total,
          current: 0,
          success: 0,
          failed: 0,
          status: 'running',
          taskId: response.task_id
        });
        
        toast({
          title: 'Sync Started',
          description: `Background sync started for ${response.total} listings`
        });
        
        // Poll for progress
        const pollInterval = setInterval(async () => {
          try {
            const status = await apiCall(`/api/shopify-sync/bulk-push-status/${response.task_id}`);
            if (status) {
              setBulkProgress(prev => ({
                ...prev,
                current: status.current || 0,
                success: status.success || 0,
                failed: status.failed || 0,
                status: status.status
              }));
              
              if (status.status === 'complete') {
                clearInterval(pollInterval);
                toast({
                  title: 'Re-sync Complete',
                  description: `${status.success}/${status.total || response.total} synced${status.failed > 0 ? `, ${status.failed} failed` : ''}`
                });
                fetchListings();
                fetchData();
              }
            }
          } catch {
            // Ignore polling errors
          }
        }, 3000);
        
        // Safety: stop polling after 10 minutes
        setTimeout(() => clearInterval(pollInterval), 600000);
      }
    } catch (error) {
      setBulkProgress({ status: 'error', error: error.message });
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  };


  const handleBulkUnpublish = async () => {
    if (selectedListings.length === 0) {
      toast({ title: 'Warning', description: 'Please select listings to unpublish', variant: 'destructive' });
      return;
    }
    
    if (!window.confirm(`Unpublish ${selectedListings.length} products from Shopify?`)) return;
    
    setBulkProgress({ total: selectedListings.length, current: 0, status: 'running' });
    
    try {
      const response = await apiCall('/api/shopify-sync/bulk-unpublish', {
        method: 'POST',
        body: JSON.stringify({
          listing_ids: selectedListings
        })
      });
      
      if (response) {
        setBulkProgress({
          total: response.total,
          success: response.success,
          failed: response.failed,
          status: 'complete'
        });
        
        toast({
          title: 'Bulk Unpublish Complete',
          description: `${response.success} succeeded, ${response.failed} failed`
        });
        
        setSelectedListings([]);
        fetchListings();
        fetchData();
      }
    } catch (error) {
      setBulkProgress({ status: 'error', error: error.message });
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  };

  // Sync all pending listings
  const handleSyncAllPending = async () => {
    if (!window.confirm(`Sync all ${connectionStatus?.pending_sync || 0} pending listings to Shopify?`)) return;
    
    setSyncing(true);
    setBulkProgress({ status: 'running', total: connectionStatus?.pending_sync || 0, success: 0, failed: 0 });
    
    try {
      const response = await apiCall('/api/shopify-sync/sync-all-pending', {
        method: 'POST'
      });
      
      if (response?.status === 'running' && response?.job_id) {
        // Poll the job status
        const jobId = response.job_id;
        setBulkProgress({ status: 'running', total: response.total, success: 0, failed: 0 });
        
        const pollInterval = setInterval(async () => {
          try {
            const jobStatus = await apiCall(`/api/shopify-sync/job/${jobId}`);
            if (jobStatus) {
              setBulkProgress({
                total: jobStatus.total || response.total,
                success: jobStatus.success || 0,
                failed: jobStatus.failed || 0,
                status: jobStatus.status
              });
              
              if (jobStatus.status === 'complete' || jobStatus.status === 'error') {
                clearInterval(pollInterval);
                setSyncing(false);
                
                const failedItems = jobStatus.results?.filter(r => r.status === 'failed') || [];
                const failSummary = failedItems.length > 0
                  ? `\nFailed: ${failedItems.slice(0, 3).map(f => `${f.title} (${f.error?.substring(0, 60)})`).join(', ')}${failedItems.length > 3 ? ` +${failedItems.length - 3} more` : ''}`
                  : '';
                
                toast({
                  title: jobStatus.status === 'complete' ? 'Sync Complete' : 'Sync Error',
                  description: `${jobStatus.success || 0} synced, ${jobStatus.failed || 0} failed out of ${jobStatus.total || response.total}${failSummary}`,
                  variant: jobStatus.failed > 0 ? 'destructive' : 'default',
                  duration: 15000
                });
                
                fetchData();
              }
            }
          } catch (pollErr) {
            // Polling error — don't stop, try again
            console.warn('Job poll error:', pollErr);
          }
        }, 3000);
        
        // Safety: stop polling after 10 minutes
        setTimeout(() => {
          clearInterval(pollInterval);
          setSyncing(false);
        }, 600000);
        
      } else if (response?.status === 'complete') {
        // Small batch completed synchronously
        setBulkProgress({
          total: response.total,
          success: response.success || 0,
          failed: response.failed || 0,
          status: 'complete'
        });
        toast({
          title: 'Sync Complete',
          description: response.message || `${response.success} synced, ${response.failed} failed out of ${response.total}`
        });
        fetchData();
        setSyncing(false);
      }
    } catch (error) {
      setBulkProgress({ status: 'error', error: error.message });
      toast({ title: 'Sync Error', description: error.message, variant: 'destructive' });
      setSyncing(false);
    }
  };

  // Migrate existing flat shopify_product_id values into per-store dict for active store
  const handleMigrateToPerStore = async () => {
    if (!window.confirm(`Migrate existing sync state to per-store tracking for ${connectionStatus?.shop_url}?\n\nThis is safe and reversible. Run this once after switching stores.`)) return;
    setSyncing(true);
    try {
      const response = await apiCall('/api/shopify-sync/migrate-to-per-store', { method: 'POST' });
      if (response?.success) {
        const errCount = response.errors?.length || 0;
        toast({ 
          title: 'Migration Complete', 
          description: `${response.message}${errCount > 0 ? ` First error: ${response.errors[0]?.error}` : ''}`,
          variant: errCount > 0 ? 'default' : 'default',
          duration: errCount > 0 ? 10000 : 5000
        });
        fetchData();
      }
    } catch (error) {
      toast({ title: 'Migration Failed', description: error.message, variant: 'destructive' });
    } finally {
      setSyncing(false);
    }
  };

  // Reset per-store sync state so all products will be re-pushed to the active store
  const handleResetStoreSync = async () => {
    if (!window.confirm(`Reset ALL sync state for ${connectionStatus?.shop_url}?\n\nThis will mark all listings as pending for THIS store only. Other stores are unaffected.\n\nUse "Sync All Pending" afterwards to push them as new items.`)) return;
    setSyncing(true);
    try {
      const response = await apiCall('/api/shopify-sync/reset-store-sync', { method: 'POST' });
      if (response?.success) {
        toast({ title: 'Reset Complete', description: response.message });
        fetchData();
      }
    } catch (error) {
      toast({ title: 'Reset Failed', description: error.message, variant: 'destructive' });
    } finally {
      setSyncing(false);
    }
  };

  // Check how CMS categories map to Shopify collections
  const handleCheckCollections = async () => {
    setSyncing(true);
    try {
      const response = await apiCall('/api/shopify-sync/check-collections', { method: 'GET' });
      if (response) {
        const msg = `Store: ${response.shop}\n\nShopify collections: ${response.shopify_collection_count}\nCMS categories: ${response.cms_category_count}\n\nMatched: ${response.matched_count}\nMissing from Shopify: ${response.missing_count}\n\nMissing (first 15):\n${(response.missing_from_shopify || []).slice(0, 15).join('\n')}`;
        alert(msg);
      }
    } catch (error) {
      toast({ title: 'Check Failed', description: error.message, variant: 'destructive' });
    } finally {
      setSyncing(false);
    }
  };

  // Auto-create missing Shopify collections based on CMS categories
  const handleSyncCmsCollections = async () => {
    if (!window.confirm(`Create missing Shopify collections for all CMS categories on ${connectionStatus?.shop_url}?\n\nThis is safe — existing collections are not modified.`)) return;
    setSyncing(true);
    try {
      const response = await apiCall('/api/shopify-sync/sync-cms-collections', { method: 'POST' });
      if (response) {
        toast({
          title: 'Collections Synced',
          description: `Created ${response.created_count} collections. ${response.failed_count} failed.`
        });
      }
    } catch (error) {
      toast({ title: 'Sync Failed', description: error.message, variant: 'destructive' });
    } finally {
      setSyncing(false);
    }
  };

  // Auto-link Shopify collections to CMS categories by title matching
  const handleAutoLinkCollections = async () => {
    setSyncing(true);
    try {
      // First do a dry run
      const preview = await apiCall('/api/shopify-sync/auto-link-collections?dry_run=true', { method: 'POST' });
      if (!preview) return;

      const catCount = preview.new_category_links || 0;
      const subCount = preview.new_subcategory_links || 0;
      const alreadyCount = preview.already_linked || 0;

      if (catCount === 0 && subCount === 0) {
        toast({ title: 'All Matched', description: `All matching collections are already linked (${alreadyCount} existing links).` });
        return;
      }

      const catNames = (preview.linked_categories || []).map(l => l.category).join(', ');
      const confirmed = window.confirm(
        `Auto-link ${catCount} categories and ${subCount} subcategories to matching Shopify collections?\n\n` +
        `Categories: ${catNames || 'None'}\n` +
        `Subcategories: ${subCount} matches found\n` +
        `Already linked: ${alreadyCount}\n\n` +
        `This is safe — existing links are not modified.`
      );
      if (!confirmed) return;

      const result = await apiCall('/api/shopify-sync/auto-link-collections?dry_run=false', { method: 'POST' });
      if (result) {
        toast({
          title: 'Collections Auto-Linked',
          description: `Linked ${result.new_category_links} categories and ${result.new_subcategory_links} subcategories.`
        });
      }
    } catch (error) {
      toast({ title: 'Auto-Link Failed', description: error.message, variant: 'destructive' });
    } finally {
      setSyncing(false);
    }
  };

  // Update all already synced listings
  const handleUpdateAllSynced = async () => {
    if (!window.confirm(`Update all ${connectionStatus?.synced_listings || 0} synced listings in Shopify?`)) return;
    
    setSyncing(true);
    setBulkProgress({ status: 'running', total: connectionStatus?.synced_listings || 0, success: 0, failed: 0 });
    
    try {
      const response = await apiCall('/api/shopify-sync/update-all-synced', {
        method: 'POST'
      });
      
      if (!response) return;

      // iter-221g — endpoint queues a background task and returns
      // {queued, total}. Poll the status endpoint for live counts so the
      // toast shows real numbers instead of "undefined".
      if (response.queued) {
        setBulkProgress({
          status: 'running',
          total: response.total || 0,
          success: 0,
          failed: 0,
        });
        toast({
          title: 'Sync queued',
          description: `Updating ${response.total} synced listings in the background. Progress will update live.`,
        });

        const pollInterval = setInterval(async () => {
          try {
            const status = await apiCall('/api/shopify-sync/update-all-synced-status');
            if (!status) return;
            setBulkProgress({
              status: status.status,
              total: status.total || 0,
              success: status.success || 0,
              failed: status.failed || 0,
            });
            if (status.status === 'complete' || status.status === 'error') {
              clearInterval(pollInterval);
              setSyncing(false);
              if (status.status === 'complete') {
                toast({
                  title: 'Update Complete',
                  description: `${status.success || 0} updated, ${status.failed || 0} failed out of ${status.total || 0}`,
                  variant: status.failed > 0 ? 'destructive' : 'default',
                });
              } else {
                toast({
                  title: 'Update Failed',
                  description: status.error || 'Background task errored — check backend logs',
                  variant: 'destructive',
                });
              }
              fetchData();
            }
          } catch (pollErr) {
            // Ignore transient poll errors — keep polling
            console.warn('[update-all-synced] poll error:', pollErr);
          }
        }, 3000);

        // Safety timeout — stop polling after 30 minutes max
        setTimeout(() => {
          clearInterval(pollInterval);
          setSyncing(false);
        }, 30 * 60 * 1000);
        return;
      }

      // Synchronous response (no listings to update / legacy path)
      setBulkProgress({
        total: response.total || 0,
        success: response.success || 0,
        failed: response.failed || 0,
        status: 'complete',
      });
      toast({
        title: 'Update Complete',
        description: response.message || `${response.success || 0} updated, ${response.failed || 0} failed out of ${response.total || 0}`,
      });
      fetchData();
      setSyncing(false);
    } catch (error) {
      setBulkProgress({ status: 'error', error: error.message });
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
      setSyncing(false);
    }
  };

  // Sync inventory levels for all active synced listings (without re-pushing products)
  const handleSyncAllInventory = async () => {
    if (!window.confirm(
      `Re-sync inventory levels for all active synced listings?\n\nThis only updates stock counts in Shopify — it does NOT re-push product details.`
    )) return;

    setInventorySyncing(true);
    setInvSyncResult(null);
    try {
      const response = await apiCall('/api/shopify-sync/sync-all-inventory', { method: 'POST' });
      if (response?.status === 'running' && response?.job_id) {
        const jobId = response.job_id;
        setInvSyncResult({
          queued: true,
          status: 'running',
          total: response.total || 0,
          current: 0,
          success: 0,
          failed: 0,
          skipped: 0,
          message: response.message || 'Inventory sync started in the background'
        });
        toast({
          title: 'Inventory Sync Started',
          description: `Syncing inventory for ${response.total} listings in background`
        });

        // Poll job progress
        const pollInterval = setInterval(async () => {
          try {
            const jobStatus = await apiCall(`/api/shopify-sync/job/${jobId}`);
            if (jobStatus) {
              setInvSyncResult(prev => ({
                ...prev,
                status: jobStatus.status,
                current: jobStatus.current || 0,
                total: jobStatus.total || response.total,
                success: jobStatus.success || 0,
                failed: jobStatus.failed || 0,
                skipped: jobStatus.skipped || 0,
                queued: jobStatus.status === 'running'
              }));

              if (jobStatus.status === 'complete' || jobStatus.status === 'failed' || jobStatus.status === 'error') {
                clearInterval(pollInterval);
                setInventorySyncing(false);
                toast({
                  title: jobStatus.status === 'complete' ? 'Inventory Sync Complete' : 'Inventory Sync Error',
                  description: `${jobStatus.success || 0} synced, ${jobStatus.failed || 0} failed, ${jobStatus.skipped || 0} skipped`,
                  variant: jobStatus.failed > 0 ? 'destructive' : 'default',
                  duration: 10000
                });
                fetchData();
              }
            }
          } catch (pollErr) {
            console.warn('Inventory job poll error:', pollErr);
          }
        }, 3000);

        // Safety: stop after 10 minutes
        setTimeout(() => {
          clearInterval(pollInterval);
          setInventorySyncing(false);
        }, 600000);
      } else {
        // No job tracking (small batch or no items)
        setInvSyncResult({
          queued: false,
          total: response?.total || 0,
          message: response?.message || 'No items to sync'
        });
        setInventorySyncing(false);
      }
    } catch (error) {
      toast({ title: 'Error', description: error.message || 'Failed to sync inventory', variant: 'destructive' });
      setInventorySyncing(false);
    }
  };

  // Retry all failed syncs — resets stuck products back to pending
  const handleRetryFailedSync = async () => {
    if (!window.confirm(
      `Reset all failed/stuck products back to pending?\n\nAfter this, run "Sync All Pending" to re-push them to Shopify.`
    )) return;

    setSyncing(true);
    try {
      const response = await apiCall('/api/shopify-sync/retry-failed', { method: 'POST' });
      if (response?.success) {
        toast({
          title: 'Failed Syncs Reset',
          description: response.message || `${response.total} products reset to pending`,
          duration: 8000
        });
        fetchData();
        fetchListings();
      } else {
        toast({
          title: 'No Failed Items',
          description: response?.message || 'No failed listings found to retry'
        });
      }
    } catch (error) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    } finally {
      setSyncing(false);
    }
  };

  // ── Remap Shopify ID Handlers ──
  const handleOpenRemapModal = async () => {
    setShowRemapModal(true);
    setRemapLoading(true);
    setRemapInputs({});
    try {
      const response = await apiCall('/api/shopify-sync/failed-listings-for-remap');
      setRemapListings(response?.listings || []);
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to load failed listings', variant: 'destructive' });
    } finally {
      setRemapLoading(false);
    }
  };

  const handleRemapInputChange = (listingId, value) => {
    setRemapInputs(prev => ({ ...prev, [listingId]: value }));
  };

  const handleBulkRemap = async () => {
    const mappings = Object.entries(remapInputs)
      .filter(([, val]) => val && val.trim())
      .map(([listing_id, shopify_product_id]) => ({ listing_id, shopify_product_id }));

    if (mappings.length === 0) {
      toast({ title: 'No mappings', description: 'Paste Shopify URLs or IDs for the listings you want to remap.' });
      return;
    }

    setRemapSaving(true);
    try {
      const response = await apiCall('/api/shopify-sync/bulk-remap-shopify-ids', {
        method: 'POST',
        body: JSON.stringify({ mappings }),
        headers: { 'Content-Type': 'application/json' }
      });
      if (response?.success) {
        toast({
          title: 'Remapped Successfully',
          description: response.message,
          duration: 8000
        });
        setShowRemapModal(false);
        fetchData();
        fetchListings();
      }
    } catch (err) {
      toast({ title: 'Remap Failed', description: err.message, variant: 'destructive' });
    } finally {
      setRemapSaving(false);
    }
  };

  // ── Fresh Push All Handler ──
  const handleFreshPushAll = async () => {
    if (freshPushConfirmText !== 'FRESH PUSH') {
      toast({ title: 'Invalid', description: 'Type "FRESH PUSH" exactly to confirm.', variant: 'destructive' });
      return;
    }
    setFreshPushRunning(true);
    try {
      const response = await apiCall('/api/shopify-sync/fresh-push-all', {
        method: 'POST',
        body: JSON.stringify({ confirmation_text: 'FRESH PUSH' }),
        headers: { 'Content-Type': 'application/json' }
      });
      if (response?.success) {
        toast({
          title: 'Fresh Push Ready',
          description: `${response.total} listings disconnected from Shopify. Now run "Sync All Pending" to create fresh products.`,
          duration: 12000
        });
        setShowFreshPushModal(false);
        setFreshPushConfirmText('');
        fetchData();
        fetchListings();
      }
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setFreshPushRunning(false);
    }
  };

  // ── Injection Status Handler ──
  const fetchInjectionStatus = async () => {
    setInjectionLoading(true);
    try {
      const response = await apiCall('/api/shopify-theme/script-status');
      setInjectionStatus(response);
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to check injection status', variant: 'destructive' });
    } finally {
      setInjectionLoading(false);
    }
  };


  // ── Inventory Audit Handler ──
  const handleInventoryAudit = async () => {
    setShowAuditModal(true);
    setAuditLoading(true);
    setAuditData(null);
    try {
      const response = await apiCall('/api/shopify-sync/inventory-audit');
      setAuditData(response);
    } catch (err) {
      toast({ title: 'Audit Failed', description: err.message, variant: 'destructive' });
    } finally {
      setAuditLoading(false);
    }
  };



  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedListings(listings.map(l => l.id));
    } else {
      setSelectedListings([]);
    }
  };

  const handleSelectListing = (listingId, checked) => {
    if (checked) {
      setSelectedListings([...selectedListings, listingId]);
    } else {
      setSelectedListings(selectedListings.filter(id => id !== listingId));
    }
  };

  // ── Per-store sync helpers ──────────────────────────────────────────────
  // Convert shop.myshopify.com → shop__myshopify__com (matches backend get_store_key)
  const getStoreKey = (shopUrl) => (shopUrl || '').replace(/\./g, '__');

  // Return the sync entry for the currently active store, or a "pending" stub if not found.
  const getListingSyncState = (listing) => {
    const shopUrl = connectionStatus?.shop_url;
    if (!shopUrl) {
      // No active store — fall back to flat fields for display
      return {
        product_id: listing.shopify_product_id,
        sync_status: listing.shopify_sync_status,
        sync_error: listing.shopify_sync_error,
        synced_at: listing.shopify_synced_at,
      };
    }
    const key = getStoreKey(shopUrl);
    const storeEntry = listing.shopify_stores?.[key];
    if (storeEntry) return storeEntry;
    // No per-store entry → treat as pending for this store
    return { product_id: null, sync_status: 'pending', sync_error: null };
  };

  const getSyncStatusBadge = (listing) => {
    const s = getListingSyncState(listing);
    if (s.sync_status?.includes('failed')) {
      const label = s.sync_status === 'validation_failed' ? 'Validation Failed'
        : s.sync_status === 'update_failed' ? 'Update Failed'
        : s.sync_status === 'conversion_failed' ? 'Conversion Failed'
        : 'Failed';
      return <Badge variant="destructive" className="text-xs"><XCircle className="h-3 w-3 mr-1" />{label}</Badge>;
    }
    if (s.sync_status === 'unpublished') {
      return <Badge variant="secondary" className="text-xs"><CloudOff className="h-3 w-3 mr-1" />Unpublished</Badge>;
    }
    if (s.sync_status === 'synced' && s.product_id) {
      return <Badge className="bg-green-100 text-green-700 text-xs"><CheckCircle className="h-3 w-3 mr-1" />Synced</Badge>;
    }
    if (s.product_id) {
      return <Badge className="bg-green-100 text-green-700 text-xs"><CheckCircle className="h-3 w-3 mr-1" />Synced</Badge>;
    }
    return <Badge variant="outline" className="text-xs"><Clock className="h-3 w-3 mr-1" />Pending</Badge>;
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Shopify Sync</h1>
            <p className="text-gray-600">Manage product synchronization with Shopify</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={fetchData}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
            <Button variant="outline" onClick={() => window.open(`https://${connectionStatus?.shop_url}/admin`, '_blank')}>
              <ExternalLink className="h-4 w-4 mr-2" />
              Open Shopify Admin
            </Button>
          </div>
        </div>

        {/* Connection Status Banner */}
        {connectionStatus && (
          <Card className={connectionStatus.connected ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'}>
            <CardContent className="py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className={`p-3 rounded-full ${connectionStatus.connected ? 'bg-green-100' : 'bg-red-100'}`}>
                    {connectionStatus.connected ? (
                      <Cloud className="h-6 w-6 text-green-600" />
                    ) : (
                      <CloudOff className="h-6 w-6 text-red-600" />
                    )}
                  </div>
                  <div>
                    <h3 className={`font-semibold ${connectionStatus.connected ? 'text-green-800' : 'text-red-800'}`}>
                      {connectionStatus.connected ? 'Connected to Shopify' : 'Not Connected'}
                    </h3>
                    <p className={`text-sm ${connectionStatus.connected ? 'text-green-600' : 'text-red-600'}`}>
                      {connectionStatus.connected 
                        ? `${connectionStatus.shop_name} (${connectionStatus.shop_url})`
                        : connectionStatus.message || 'Please configure Shopify credentials'}
                    </p>
                  </div>
                </div>
                {connectionStatus.connected && (
                  <div className="flex gap-6 text-sm">
                    <div className="text-center">
                      <p className="text-2xl font-bold text-gray-900">{connectionStatus.total_listings}</p>
                      <p className="text-gray-500">Total Active</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-green-600">{connectionStatus.synced_listings}</p>
                      <p className="text-gray-500">Synced</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-amber-600">{connectionStatus.pending_sync}</p>
                      <p className="text-gray-500">Pending</p>
                    </div>
                    {connectionStatus.failed_listings > 0 && (
                      <div className="text-center">
                        <p className="text-2xl font-bold text-red-600">{connectionStatus.failed_listings}</p>
                        <p className="text-gray-500">Failed</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Webhook Secret — status-aware: green when active, amber when disabled */}
        {connectionStatus?.connected && (
          connectionStatus?.has_shared_secret ? (
            <div className="flex items-center gap-3 px-4 py-3 bg-green-50 border border-green-200 rounded-lg text-sm" data-testid="webhook-hmac-active">
              <CheckCircle className="h-4 w-4 text-green-600 shrink-0" />
              <span className="text-green-800 font-medium">Webhook HMAC validation is active</span>
              <Button
                size="sm"
                variant="ghost"
                className="ml-auto h-7 text-xs text-green-700 hover:text-red-600"
                onClick={async () => {
                  if (!window.confirm('Remove webhook signing secret? This will disable HMAC validation.')) return;
                  try {
                    await apiCall('/api/shopify-app/update-credentials', {
                      method: 'PUT',
                      body: JSON.stringify({ shop_url: connectionStatus.shop_url, shared_secret: '' })
                    });
                    toast({ title: 'Secret Removed', description: 'Webhook HMAC validation is now disabled.' });
                    fetchData();
                  } catch (e) {
                    toast({ title: 'Error', description: e.message, variant: 'destructive' });
                  }
                }}
              >
                Reset Secret
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-3 px-4 py-3 bg-amber-50 border border-amber-200 rounded-lg text-sm" data-testid="webhook-hmac-disabled">
              <Shield className="h-4 w-4 text-amber-600 shrink-0" />
              <span className="text-amber-800">Webhook HMAC validation is disabled — add a signing secret for security.</span>
              <div className="flex items-center gap-2 ml-auto">
                <Input
                  type="password"
                  placeholder="Webhook signing secret"
                  className="w-56 h-8 text-xs"
                  value={webhookSecret}
                  onChange={(e) => setWebhookSecret(e.target.value)}
                  data-testid="webhook-secret-update-input"
                />
                <Button
                  size="sm"
                  variant="outline"
                  className="h-8 text-xs border-amber-300"
                  disabled={!webhookSecret}
                  onClick={async () => {
                    try {
                      const res = await apiCall('/api/shopify-app/update-credentials', {
                        method: 'PUT',
                        body: JSON.stringify({ shop_url: connectionStatus.shop_url, shared_secret: webhookSecret })
                      });
                      if (res?.success) {
                        toast({ title: 'Secret Saved', description: 'Webhook HMAC validation is now active.' });
                        setWebhookSecret('');
                        fetchData();
                      }
                    } catch (e) {
                      toast({ title: 'Error', description: e.message, variant: 'destructive' });
                    }
                  }}
                  data-testid="save-webhook-secret-btn"
                >
                  Save
                </Button>
              </div>
            </div>
          )
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="overview">
              <Store className="h-4 w-4 mr-2" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="listings">
              <Package className="h-4 w-4 mr-2" />
              Listings
            </TabsTrigger>
            <TabsTrigger value="history">
              <Clock className="h-4 w-4 mr-2" />
              Sync History
            </TabsTrigger>
            <TabsTrigger value="settings">
              <Settings className="h-4 w-4 mr-2" />
              Settings
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="mt-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Quick Actions */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Quick Actions</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <Button 
                    className="w-full justify-start" 
                    onClick={handleSyncAllPending}
                    disabled={syncing || !connectionStatus?.connected}
                    data-testid="sync-all-pending-btn"
                  >
                    {syncing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
                    Sync All Pending ({connectionStatus?.pending_sync || 0})
                  </Button>
                  <Button 
                    variant="outline" 
                    className="w-full justify-start"
                    onClick={handleUpdateAllSynced}
                    disabled={syncing || !connectionStatus?.connected}
                    data-testid="update-all-synced-btn"
                  >
                    {syncing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
                    Update All Synced ({connectionStatus?.synced_listings || 0})
                  </Button>
                  <Button 
                    variant="outline"
                    className="w-full justify-start"
                    onClick={handleSyncAllInventory}
                    disabled={inventorySyncing || !connectionStatus?.connected}
                    data-testid="sync-all-inventory-btn"
                  >
                    {inventorySyncing
                      ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      : <BarChart2 className="h-4 w-4 mr-2" />
                    }
                    Sync All Inventory ({connectionStatus?.synced_listings || 0})
                  </Button>
                  <Button 
                    variant="outline" 
                    className="w-full justify-start text-teal-700 border-teal-200 hover:bg-teal-50 mt-2"
                    onClick={handleInventoryAudit}
                    disabled={syncing || !connectionStatus?.connected}
                    data-testid="inventory-audit-btn"
                  >
                    <Activity className="h-4 w-4 mr-2" />
                    Inventory Audit (Portal vs Shopify)
                  </Button>
                  {invSyncResult && (
                    <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1">
                          {invSyncResult.queued || invSyncResult.status === 'running' ? (
                            <>
                              <div className="flex items-center gap-2">
                                <Loader2 className="h-4 w-4 animate-spin text-blue-600 flex-shrink-0" />
                                <p className="font-medium text-blue-800">
                                  Syncing inventory... {invSyncResult.current || 0}/{invSyncResult.total || '?'}
                                </p>
                              </div>
                              {invSyncResult.success > 0 && (
                                <p className="text-blue-600 text-xs mt-1 ml-6">
                                  {invSyncResult.success} ok, {invSyncResult.failed} failed, {invSyncResult.skipped} skipped
                                </p>
                              )}
                              {invSyncResult.total > 0 && (
                                <div className="mt-2 w-full bg-blue-200 rounded-full h-2">
                                  <div 
                                    className="bg-blue-600 h-2 rounded-full transition-all duration-500"
                                    style={{ width: `${Math.min(100, ((invSyncResult.current || 0) / invSyncResult.total) * 100)}%` }}
                                  />
                                </div>
                              )}
                            </>
                          ) : invSyncResult.status === 'complete' ? (
                            <div className="flex items-center gap-2">
                              <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                              <p className="font-medium text-green-800">
                                {invSyncResult.success ?? 0} updated · {invSyncResult.failed ?? 0} failed · {invSyncResult.skipped ?? 0} skipped
                              </p>
                            </div>
                          ) : invSyncResult.status === 'failed' || invSyncResult.status === 'error' ? (
                            <div className="flex items-center gap-2">
                              <XCircle className="h-4 w-4 text-red-600 flex-shrink-0" />
                              <p className="font-medium text-red-800">
                                Inventory sync failed — {invSyncResult.success ?? 0} succeeded before error
                              </p>
                            </div>
                          ) : (
                            <p className="font-medium text-blue-800">
                              {invSyncResult.message || 'No items to sync'}
                            </p>
                          )}
                        </div>
                        <button
                          onClick={() => setInvSyncResult(null)}
                          className="text-blue-500 hover:text-blue-700 flex-shrink-0"
                          data-testid="dismiss-inv-result-btn"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  )}
                  <Button 
                    variant="outline" 
                    className="w-full justify-start text-orange-700 border-orange-200 hover:bg-orange-50"
                    onClick={handleRetryFailedSync}
                    disabled={syncing || !connectionStatus?.connected}
                    data-testid="retry-failed-sync-btn"
                  >
                    {syncing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
                    Retry Failed Syncs ({connectionStatus?.failed_listings || 0})
                  </Button>
                  <Button 
                    variant="outline" 
                    className="w-full justify-start"
                    onClick={() => {
                      setActiveTab('listings');
                      setSyncFilter('failed');
                    }}
                  >
                    <AlertTriangle className="h-4 w-4 mr-2" />
                    View Failed Syncs
                  </Button>
                  <Button 
                    variant="outline" 
                    className="w-full justify-start text-purple-700 border-purple-200 hover:bg-purple-50 mt-2"
                    onClick={handleOpenRemapModal}
                    disabled={syncing || !connectionStatus?.connected}
                    data-testid="remap-shopify-ids-btn"
                  >
                    <ExternalLink className="h-4 w-4 mr-2" />
                    Remap Shopify IDs (Failed)
                  </Button>
                  <Button 
                    variant="outline" 
                    className="w-full justify-start text-red-700 border-red-200 hover:bg-red-50 mt-2"
                    onClick={() => { setShowFreshPushModal(true); setFreshPushConfirmText(''); }}
                    disabled={syncing || !connectionStatus?.connected}
                    data-testid="fresh-push-all-btn"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Fresh Push All (Reset)
                  </Button>
                  <div className="border-t pt-3 mt-1">
                    <p className="text-xs text-gray-500 mb-2 font-medium">Multi-Store Tools</p>
                    <Button
                      variant="outline"
                      className="w-full justify-start text-blue-700 border-blue-200 hover:bg-blue-50"
                      onClick={handleMigrateToPerStore}
                      disabled={syncing || !connectionStatus?.connected}
                      data-testid="migrate-per-store-btn"
                    >
                      <RefreshCw className="h-4 w-4 mr-2" />
                      Migrate to Per-Store
                    </Button>
                    <Button
                      variant="outline"
                      className="w-full justify-start text-red-700 border-red-200 hover:bg-red-50 mt-2"
                      onClick={handleResetStoreSync}
                      disabled={syncing || !connectionStatus?.connected}
                      data-testid="reset-store-sync-btn"
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Reset Store Sync
                    </Button>
                  </div>
                  <div className="border-t pt-3 mt-1">
                    <p className="text-xs text-gray-500 mb-2 font-medium">Collections</p>
                    <Button
                      variant="outline"
                      className="w-full justify-start text-purple-700 border-purple-200 hover:bg-purple-50"
                      onClick={handleCheckCollections}
                      disabled={syncing || !connectionStatus?.connected}
                      data-testid="check-collections-btn"
                    >
                      <Search className="h-4 w-4 mr-2" />
                      Check Collection Mapping
                    </Button>
                    <Button
                      variant="outline"
                      className="w-full justify-start text-green-700 border-green-200 hover:bg-green-50 mt-2"
                      onClick={handleSyncCmsCollections}
                      disabled={syncing || !connectionStatus?.connected}
                      data-testid="sync-collections-btn"
                    >
                      <Folder className="h-4 w-4 mr-2" />
                      Create Missing Collections
                    </Button>
                    <Button
                      variant="outline"
                      className="w-full justify-start text-blue-700 border-blue-200 hover:bg-blue-50 mt-2"
                      onClick={handleAutoLinkCollections}
                      disabled={syncing || !connectionStatus?.connected}
                      data-testid="auto-link-collections-btn"
                    >
                      <Link2 className="h-4 w-4 mr-2" />
                      Auto-Link by Title Match
                    </Button>
                  </div>
                  <div className="border-t pt-3 mt-1">
                    <p className="text-xs text-gray-500 mb-2 font-medium">Storefront Script</p>
                    <Button
                      variant="outline"
                      className="w-full justify-start text-indigo-700 border-indigo-200 hover:bg-indigo-50"
                      onClick={handleInjectToTheme}
                      disabled={!connectionStatus?.connected}
                      data-testid="inject-theme-btn"
                    >
                      <Code className="h-4 w-4 mr-2" />
                      Inject into Theme (OS 2.0)
                    </Button>
                    <Button
                      variant="outline"
                      className="w-full justify-start text-red-700 border-red-200 hover:bg-red-50 mt-2"
                      onClick={handleRemoveFromTheme}
                      disabled={!connectionStatus?.connected}
                      data-testid="remove-theme-injection-btn"
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Remove from Theme.liquid
                    </Button>
                    <Button
                      variant="outline"
                      className="w-full justify-start text-gray-700 border-gray-200 hover:bg-gray-50 mt-2"
                      onClick={handleCheckScriptStatus}
                      disabled={!connectionStatus?.connected}
                      data-testid="check-script-status-btn"
                    >
                      <Activity className="h-4 w-4 mr-2" />
                      Check Script Status
                    </Button>
                    <Button
                      variant="outline"
                      className="w-full justify-start text-gray-700 border-gray-200 hover:bg-gray-50 mt-2"
                      onClick={handleReinstallScript}
                      disabled={!connectionStatus?.connected}
                      data-testid="reinstall-script-btn"
                    >
                      <Upload className="h-4 w-4 mr-2" />
                      Reinstall ScriptTag (Legacy)
                    </Button>
                    <Button
                      variant="outline"
                      className="w-full justify-start text-red-600 border-red-200 hover:bg-red-50 mt-2"
                      onClick={async () => {
                        try {
                          const res = await apiCall('/api/shopify-theme/uninstall-script', { method: 'DELETE' });
                          toast({ title: 'ScriptTags Removed', description: res?.message || 'Old ScriptTag registrations cleaned up.' });
                        } catch (e) {
                          toast({ title: 'Error', description: e.message, variant: 'destructive' });
                        }
                      }}
                      disabled={!connectionStatus?.connected}
                      data-testid="remove-scripttags-btn"
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Remove Old ScriptTags
                    </Button>
                    {/* ── Liquid Audit & Variant JSON Patch ── */}
                    <div className="border-t pt-2 mt-2">
                      <p className="text-xs text-gray-400 font-medium mb-2">Liquid Template Diagnostics</p>
                      <Button
                        variant="outline"
                        className="w-full justify-start text-violet-700 border-violet-200 hover:bg-violet-50"
                        onClick={handleLiquidAudit}
                        disabled={!connectionStatus?.connected || liquidAuditLoading}
                        data-testid="liquid-audit-btn"
                      >
                        {liquidAuditLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Activity className="h-4 w-4 mr-2" />}
                        Liquid Audit (PDP vs Quick View)
                      </Button>
                      <Button
                        variant="outline"
                        className="w-full justify-start text-blue-700 border-blue-200 hover:bg-blue-50 mt-2"
                        onClick={handleRenderChainAudit}
                        disabled={!connectionStatus?.connected || renderChainLoading}
                        data-testid="render-chain-audit-btn"
                      >
                        {renderChainLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Search className="h-4 w-4 mr-2" />}
                        Render Chain RCA (Root Cause)
                      </Button>
                      <Button
                        variant="outline"
                        className="w-full justify-start text-orange-700 border-orange-200 hover:bg-orange-50 mt-2"
                        onClick={handlePatchVariantJson}
                        disabled={!connectionStatus?.connected || patchingVariantJson}
                        data-testid="patch-variant-json-btn"
                      >
                        {patchingVariantJson ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Code className="h-4 w-4 mr-2" />}
                        Patch: Inject variant-json Blocks
                      </Button>
                    </div>
                  </div>

                  {/* ── Injection Status Panel ── */}
                  <div className="border-t pt-3 mt-3">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs text-gray-500 font-medium">Injection Status</p>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-6 text-xs"
                        onClick={fetchInjectionStatus}
                        disabled={injectionLoading || !connectionStatus?.connected}
                      >
                        {injectionLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
                      </Button>
                    </div>
                    {injectionStatus ? (
                      <div className="space-y-2 text-xs">
                        {/* Method */}
                        <div className="flex items-center gap-2">
                          {injectionStatus.installation_method !== 'none' ? (
                            <CheckCircle className="h-3.5 w-3.5 text-green-500 shrink-0" />
                          ) : (
                            <XCircle className="h-3.5 w-3.5 text-red-500 shrink-0" />
                          )}
                          <span className={injectionStatus.installation_method !== 'none' ? 'text-green-700 font-medium' : 'text-red-600'}>
                            {injectionStatus.installation_method === 'theme_liquid' && 'Active via theme.liquid injection'}
                            {injectionStatus.installation_method === 'app_embed' && 'Active via App Embed (recommended)'}
                            {injectionStatus.installation_method === 'script_tag' && 'Active via ScriptTag (legacy)'}
                            {injectionStatus.installation_method === 'none' && 'Not installed'}
                          </span>
                        </div>
                        {/* Theme */}
                        {injectionStatus.active_theme?.name && (
                          <div className="flex items-center gap-2 text-gray-500">
                            <Globe className="h-3 w-3 shrink-0" />
                            <span>Theme: {injectionStatus.active_theme.name}</span>
                          </div>
                        )}
                        {/* Heartbeat */}
                        {injectionStatus.heartbeat ? (
                          <div className="flex items-center gap-2 text-green-600">
                            <Activity className="h-3 w-3 shrink-0" />
                            <span>Heartbeat: active (last seen {new Date(injectionStatus.heartbeat.last_seen || injectionStatus.heartbeat.timestamp).toLocaleDateString()})</span>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2 text-amber-600">
                            <Activity className="h-3 w-3 shrink-0" />
                            <span>No heartbeat received yet</span>
                          </div>
                        )}
                        {/* Duplicate check */}
                        <div className="flex items-center gap-2">
                          {injectionStatus.theme_injection?.found && injectionStatus.app_embed?.found ? (
                            <>
                              <XCircle className="h-3.5 w-3.5 text-red-500 shrink-0" />
                              <span className="text-red-600 font-medium">Duplicate injection detected — theme.liquid + App Embed both active</span>
                            </>
                          ) : (
                            <>
                              <CheckCircle className="h-3 w-3 shrink-0 text-gray-400" />
                              <span className="text-gray-500">No duplicate injections</span>
                            </>
                          )}
                        </div>
                        {injectionStatus.theme_injection?.found && injectionStatus.app_embed?.found && (
                          <div className="bg-red-50 border border-red-200 rounded p-2 mt-1">
                            <p className="text-red-700 text-xs font-medium">Action required: Click "Remove from Theme.liquid" above to fix variant price and add-to-cart bugs caused by dual script execution.</p>
                          </div>
                        )}
                        {/* Re-injection note */}
                        <p className="text-gray-400 pt-1 border-t mt-1">Re-injection only needed if you change your Shopify theme.</p>
                        {/* Issues */}
                        {injectionStatus.issues?.length > 0 && (
                          <div className="mt-1 space-y-1">
                            {injectionStatus.issues.map((issue, i) => (
                              <div key={i} className="flex items-start gap-1 text-amber-600">
                                <AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" />
                                <span>{issue}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ) : (
                      <p className="text-xs text-gray-400">Click refresh to check injection status</p>
                    )}
                  </div>
                </CardContent>
              </Card>


              {/* Sync Stats */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Sync Statistics</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">Sync Rate</span>
                      <span className="font-semibold">
                        {connectionStatus?.total_listings > 0 
                          ? Math.round((connectionStatus?.synced_listings / connectionStatus?.total_listings) * 100)
                          : 0}%
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-green-600 h-2 rounded-full transition-all"
                        style={{ 
                          width: `${connectionStatus?.total_listings > 0 
                            ? (connectionStatus?.synced_listings / connectionStatus?.total_listings) * 100 
                            : 0}%` 
                        }}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4 pt-2">
                      <div className="text-center p-3 bg-green-50 rounded-lg">
                        <CheckCircle className="h-5 w-5 text-green-600 mx-auto mb-1" />
                        <p className="text-lg font-bold text-green-700">{connectionStatus?.synced_listings || 0}</p>
                        <p className="text-xs text-green-600">Synced</p>
                      </div>
                      <div className="text-center p-3 bg-amber-50 rounded-lg">
                        <Clock className="h-5 w-5 text-amber-600 mx-auto mb-1" />
                        <p className="text-lg font-bold text-amber-700">{connectionStatus?.pending_sync || 0}</p>
                        <p className="text-xs text-amber-600">Pending</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Recent Activity */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Recent Activity</CardTitle>
                </CardHeader>
                <CardContent>
                  {syncHistory.length > 0 ? (
                    <div className="space-y-3">
                      {syncHistory.slice(0, 5).map((item, idx) => (
                        <div key={item.id || item.name || `${item.title || "item"}-${idx}`} className="flex items-center gap-3 text-sm">
                          {item.status === 'success' ? (
                            <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
                          ) : (
                            <XCircle className="h-4 w-4 text-red-500 flex-shrink-0" />
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="truncate font-medium">{item.title}</p>
                            <p className="text-xs text-gray-500">{item.action}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-sm text-center py-4">No recent activity</p>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Listings Tab */}
          <TabsContent value="listings" className="mt-6">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Product Listings</CardTitle>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={handleResyncAll} data-testid="resync-all-btn">
                      <RefreshCw className="h-4 w-4 mr-2" />
                      Re-sync All to Shopify
                    </Button>
                    {selectedListings.length > 0 && (
                      <>
                        <Button variant="outline" size="sm" onClick={handleBulkUnpublish}>
                          <CloudOff className="h-4 w-4 mr-2" />
                          Unpublish ({selectedListings.length})
                        </Button>
                        <Button size="sm" onClick={handleBulkPush}>
                          <Upload className="h-4 w-4 mr-2" />
                          Push to Shopify ({selectedListings.length})
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {/* Bulk Progress */}
                {bulkProgress && bulkProgress.status === 'running' && (
                  <div className="mb-4 p-4 bg-blue-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
                      <span className="text-blue-700">
                        Syncing... {bulkProgress.current || 0}/{bulkProgress.total || '?'} 
                        {bulkProgress.success > 0 && ` (${bulkProgress.success} ok, ${bulkProgress.failed} failed)`}
                      </span>
                    </div>
                    {bulkProgress.total > 0 && (
                      <div className="mt-2 w-full bg-blue-200 rounded-full h-2">
                        <div 
                          className="bg-blue-600 h-2 rounded-full transition-all duration-500"
                          style={{ width: `${Math.min(100, ((bulkProgress.current || 0) / bulkProgress.total) * 100)}%` }}
                        />
                      </div>
                    )}
                  </div>
                )}
                
                {bulkProgress && bulkProgress.status === 'complete' && (
                  <div className="mb-4 p-4 bg-green-50 rounded-lg flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <CheckCircle className="h-5 w-5 text-green-600" />
                      <span className="text-green-700">
                        Completed: {bulkProgress.success} succeeded, {bulkProgress.failed} failed
                      </span>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => setBulkProgress(null)}>
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                )}

                {/* Filters */}
                <div className="flex gap-4 mb-6 flex-wrap">
                  <div className="flex-1 relative min-w-[200px]">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      placeholder="Search listings..."
                      value={listingSearch}
                      onChange={(e) => setListingSearch(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                  <select
                    value={syncFilter}
                    onChange={(e) => setSyncFilter(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-md min-w-[150px]"
                  >
                    <option value="all">All Listings</option>
                    <option value="synced">Synced</option>
                    <option value="pending">Pending</option>
                    <option value="failed">Failed</option>
                  </select>
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-md min-w-[150px]"
                  >
                    <option value="updated_at">Sort: Last Updated</option>
                    <option value="created_at">Sort: Date Created</option>
                    <option value="shopify_synced_at">Sort: Synced Date</option>
                    <option value="title">Sort: Title</option>
                    <option value="price">Sort: Price</option>
                    <option value="vendor">Sort: Vendor</option>
                  </select>
                  <select
                    value={sortOrder}
                    onChange={(e) => setSortOrder(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-md min-w-[100px]"
                  >
                    <option value="desc">Newest</option>
                    <option value="asc">Oldest</option>
                  </select>
                </div>

                {/* Listings Table */}
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left">
                          <Checkbox 
                            checked={selectedListings.length === listings.length && listings.length > 0}
                            onCheckedChange={handleSelectAll}
                          />
                        </th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Product</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Vendor</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Price</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Sync Status</th>
                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Last Synced</th>
                        <th className="px-4 py-3 text-right text-sm font-medium text-gray-600">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {listings.length === 0 ? (
                        <tr>
                          <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                            No listings found
                          </td>
                        </tr>
                      ) : (
                        listings.map((listing) => (
                          <tr key={listing.id} className="hover:bg-gray-50">
                            <td className="px-4 py-3">
                              <Checkbox 
                                checked={selectedListings.includes(listing.id)}
                                onCheckedChange={(checked) => handleSelectListing(listing.id, checked)}
                              />
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-3">
                                <div className="h-10 w-10 bg-gray-100 rounded flex items-center justify-center overflow-hidden">
                                  {listing.media?.[0] ? (
                                    <img src={listing.media[0]} alt="" className="h-full w-full object-cover" />
                                  ) : (
                                    <Package className="h-5 w-5 text-gray-400" />
                                  )}
                                </div>
                                <div>
                                  <p className="font-medium text-gray-900 line-clamp-1">{listing.title}</p>
                                  <p className="text-xs text-gray-500">{listing.type || 'product'}</p>
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600">
                              {listing.vendor_name || '-'}
                            </td>
                            <td className="px-4 py-3">
                              <span className="font-medium">
                                ₹{(listing.bumped_price || listing.aniyor_listing_price || listing.data?.price || listing.price || 0).toLocaleString()}
                                {listing.aniyor_price_unit && (
                                  <span className="text-xs text-gray-500 ml-0.5">{listing.aniyor_price_unit}</span>
                                )}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              {getSyncStatusBadge(listing)}
                              {getListingSyncState(listing).sync_error && (
                                <p className="text-xs text-red-500 mt-1 truncate max-w-[150px]" title={getListingSyncState(listing).sync_error}>
                                  {getListingSyncState(listing).sync_error}
                                </p>
                              )}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-500">
                              {getListingSyncState(listing).synced_at
                                ? new Date(getListingSyncState(listing).synced_at).toLocaleDateString()
                                : '-'}
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex items-center justify-end gap-1">
                                {getListingSyncState(listing).product_id ? (
                                  <>
                                    <Button 
                                      variant="ghost" 
                                      size="sm"
                                      onClick={() => window.open(`https://${connectionStatus?.shop_url}/admin/products/${getListingSyncState(listing).product_id}`, '_blank')}
                                      title="View in Shopify"
                                    >
                                      <ExternalLink className="h-4 w-4" />
                                    </Button>
                                    <Button 
                                      variant="ghost" 
                                      size="sm"
                                      onClick={() => handlePushToShopify(listing.id)}
                                      disabled={syncing}
                                      title="Update in Shopify"
                                    >
                                      <RefreshCw className="h-4 w-4" />
                                    </Button>
                                    <Button 
                                      variant="ghost" 
                                      size="sm"
                                      onClick={() => handleUnpublish(listing.id)}
                                      disabled={syncing}
                                      title="Unpublish"
                                    >
                                      <CloudOff className="h-4 w-4 text-amber-500" />
                                    </Button>
                                    <Button 
                                      variant="ghost" 
                                      size="sm"
                                      onClick={() => handleRemove(listing.id)}
                                      disabled={syncing}
                                      title="Remove from Shopify"
                                    >
                                      <Trash2 className="h-4 w-4 text-red-500" />
                                    </Button>
                                  </>
                                ) : (
                                  <Button 
                                    variant="outline" 
                                    size="sm"
                                    onClick={() => handlePushToShopify(listing.id)}
                                    disabled={syncing}
                                  >
                                    <Upload className="h-4 w-4 mr-1" />
                                    Push
                                  </Button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {totalListings > 20 && (
                  <div className="flex items-center justify-between mt-4 pt-4 border-t">
                    <p className="text-sm text-gray-600">
                      Showing {(listingPage - 1) * 20 + 1} to {Math.min(listingPage * 20, totalListings)} of {totalListings}
                    </p>
                    <div className="flex gap-2">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => setListingPage(p => Math.max(1, p - 1))}
                        disabled={listingPage === 1}
                      >
                        Previous
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => setListingPage(p => p + 1)}
                        disabled={listingPage * 20 >= totalListings}
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* History Tab */}
          <TabsContent value="history" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>Sync History</CardTitle>
              </CardHeader>
              <CardContent>
                {syncHistory.length > 0 ? (
                  <div className="space-y-3">
                    {syncHistory.map((item, idx) => (
                      <div 
                        key={item.id || item.name || `${item.title || "item"}-${idx}`} 
                        className={`p-4 rounded-lg border ${
                          item.status === 'success' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex items-start gap-3">
                            {item.status === 'success' ? (
                              <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
                            ) : (
                              <XCircle className="h-5 w-5 text-red-600 mt-0.5" />
                            )}
                            <div>
                              <p className="font-medium">{item.title}</p>
                              <p className="text-sm text-gray-600">{item.action}</p>
                              {item.error && (
                                <p className="text-sm text-red-600 mt-1">{item.error}</p>
                              )}
                            </div>
                          </div>
                          <span className="text-xs text-gray-500">
                            {item.timestamp ? new Date(item.timestamp).toLocaleString() : '-'}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Clock className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">No sync history available</p>
                    <p className="text-sm text-gray-400 mt-1">
                      Sync history will appear here after you push products to Shopify
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings" className="mt-6">
            <div className="space-y-6">
              {/* Auto-Sync Settings Card */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <RefreshCw className="h-5 w-5 text-green-600" />
                    Auto-Sync Settings
                  </CardTitle>
                  <CardDescription>
                    Configure automatic synchronization of approved listings to Shopify
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Main Auto-Sync Toggle */}
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border">
                    <div className="flex items-center gap-4">
                      <div className={`p-3 rounded-full ${syncSettings?.auto_sync_on_approval ? 'bg-green-100' : 'bg-gray-200'}`}>
                        <Cloud className={`h-6 w-6 ${syncSettings?.auto_sync_on_approval ? 'text-green-600' : 'text-gray-400'}`} />
                      </div>
                      <div>
                        <Label htmlFor="auto-sync-approval" className="text-base font-semibold cursor-pointer">
                          Auto-Sync on Approval
                        </Label>
                        <p className="text-sm text-gray-500 mt-0.5">
                          {syncSettings?.auto_sync_on_approval 
                            ? 'Listings are automatically synced to Shopify when admin approves them' 
                            : 'Enable to automatically sync listings when approved by admin'}
                        </p>
                      </div>
                    </div>
                    <Switch
                      id="auto-sync-approval"
                      checked={syncSettings?.auto_sync_on_approval || false}
                      onCheckedChange={(checked) => handleUpdateSyncSettings({ auto_sync_on_approval: checked })}
                      disabled={syncSettingsLoading || !connectionStatus?.connected}
                      data-testid="auto-sync-toggle"
                    />
                  </div>

                  {/* Sync Mode Selection */}
                  <div className="space-y-3">
                    <Label className="text-sm font-medium">Sync Mode</Label>
                    <div className="grid grid-cols-3 gap-3">
                      <button
                        className={`p-4 rounded-lg border text-left transition-all ${
                          syncSettings?.sync_mode === 'manual' 
                            ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200' 
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                        onClick={() => handleUpdateSyncSettings({ sync_mode: 'manual' })}
                        disabled={syncSettingsLoading}
                      >
                        <div className="font-medium text-gray-900">Manual</div>
                        <p className="text-xs text-gray-500 mt-1">Sync each listing manually</p>
                      </button>
                      <button
                        className={`p-4 rounded-lg border text-left transition-all ${
                          syncSettings?.sync_mode === 'auto' 
                            ? 'border-green-500 bg-green-50 ring-2 ring-green-200' 
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                        onClick={() => handleUpdateSyncSettings({ sync_mode: 'auto', auto_sync_on_approval: true })}
                        disabled={syncSettingsLoading}
                      >
                        <div className="font-medium text-gray-900">Auto</div>
                        <p className="text-xs text-gray-500 mt-1">Sync on approval automatically</p>
                      </button>
                      <button
                        className={`p-4 rounded-lg border text-left transition-all ${
                          syncSettings?.sync_mode === 'scheduled' 
                            ? 'border-purple-500 bg-purple-50 ring-2 ring-purple-200' 
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                        onClick={() => handleUpdateSyncSettings({ sync_mode: 'scheduled' })}
                        disabled={syncSettingsLoading}
                      >
                        <div className="font-medium text-gray-900">Scheduled</div>
                        <p className="text-xs text-gray-500 mt-1">Sync at scheduled times</p>
                      </button>
                    </div>
                  </div>

                  {/* Schedule Configuration - Only show when scheduled mode is selected */}
                  {syncSettings?.sync_mode === 'scheduled' && (
                    <div className="space-y-4 pt-4 border-t">
                      <Label className="text-sm font-medium">Sync Schedule</Label>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="sync-frequency" className="text-xs text-gray-500">Frequency</Label>
                          <select
                            id="sync-frequency"
                            className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md"
                            value={syncSettings?.sync_frequency || 'daily'}
                            onChange={(e) => handleUpdateSyncSettings({ sync_frequency: e.target.value })}
                            disabled={syncSettingsLoading}
                          >
                            <option value="hourly">Every Hour</option>
                            <option value="every_4_hours">Every 4 Hours</option>
                            <option value="every_6_hours">Every 6 Hours</option>
                            <option value="every_12_hours">Every 12 Hours</option>
                            <option value="daily">Daily</option>
                            <option value="weekly">Weekly</option>
                          </select>
                        </div>
                        <div>
                          <Label htmlFor="sync-time" className="text-xs text-gray-500">Preferred Time (IST)</Label>
                          <select
                            id="sync-time"
                            className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md"
                            value={syncSettings?.sync_time || '00:00'}
                            onChange={(e) => handleUpdateSyncSettings({ sync_time: e.target.value })}
                            disabled={syncSettingsLoading}
                          >
                            <option value="00:00">12:00 AM</option>
                            <option value="02:00">2:00 AM</option>
                            <option value="04:00">4:00 AM</option>
                            <option value="06:00">6:00 AM</option>
                            <option value="08:00">8:00 AM</option>
                            <option value="10:00">10:00 AM</option>
                            <option value="12:00">12:00 PM</option>
                            <option value="14:00">2:00 PM</option>
                            <option value="16:00">4:00 PM</option>
                            <option value="18:00">6:00 PM</option>
                            <option value="20:00">8:00 PM</option>
                            <option value="22:00">10:00 PM</option>
                          </select>
                        </div>
                      </div>
                      
                      {/* Schedule Status Display */}
                      {scheduleStatus?.scheduled_sync_enabled && (
                        <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className="p-2 bg-purple-100 rounded-full">
                                <Clock className="h-5 w-5 text-purple-600" />
                              </div>
                              <div>
                                <p className="font-medium text-purple-900">Next Scheduled Sync</p>
                                <p className="text-sm text-purple-700">{scheduleStatus.next_sync_time_ist}</p>
                                {scheduleStatus.last_scheduled_sync && (
                                  <p className="text-xs text-purple-500 mt-1">
                                    Last sync: {new Date(scheduleStatus.last_scheduled_sync).toLocaleString()}
                                  </p>
                                )}
                              </div>
                            </div>
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={handleTriggerScheduledSync}
                              disabled={syncing}
                              className="border-purple-300 text-purple-700 hover:bg-purple-100"
                            >
                              {syncing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1" />}
                              Run Now
                            </Button>
                          </div>
                        </div>
                      )}
                      
                      <p className="text-xs text-gray-500">
                        Scheduled sync will run at the specified time and push all pending listings to Shopify automatically.
                      </p>
                    </div>
                  )}

                  {/* Sync Options */}
                  <div className="space-y-4 pt-4 border-t">
                    <Label className="text-sm font-medium">Sync Options</Label>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-sm">Sync Inventory Changes</p>
                          <p className="text-xs text-gray-500">Automatically update Shopify when inventory changes</p>
                        </div>
                        <Switch
                          checked={syncSettings?.auto_sync_inventory || false}
                          onCheckedChange={(checked) => handleUpdateSyncSettings({ auto_sync_inventory: checked })}
                          disabled={syncSettingsLoading}
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-sm">Sync Price Changes</p>
                          <p className="text-xs text-gray-500">Automatically update Shopify when prices change</p>
                        </div>
                        <Switch
                          checked={syncSettings?.auto_sync_price || false}
                          onCheckedChange={(checked) => handleUpdateSyncSettings({ auto_sync_price: checked })}
                          disabled={syncSettingsLoading}
                        />
                      </div>
                    </div>
                  </div>

                  {!connectionStatus?.connected && (
                    <Alert variant="destructive">
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>
                        Please connect to Shopify first to configure auto-sync settings.
                      </AlertDescription>
                    </Alert>
                  )}
                </CardContent>
              </Card>

              {/* Connected Stores Card */}
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <Store className="h-5 w-5 text-indigo-600" />
                        Connected Stores
                      </CardTitle>
                      <CardDescription>
                        Stores where Marketplace Connect App is installed
                      </CardDescription>
                    </div>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setShowStoreModal(true)}
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Add Store
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Store Selector Dropdown */}
                  {installedStores.length > 0 && (
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Select Store for Sync</Label>
                      <select
                        className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white"
                        value={connectionStatus?.shop_url || ''}
                        onChange={(e) => handleSwitchStore(e.target.value)}
                        disabled={switchingStore}
                      >
                        <option value="">Select a store...</option>
                        {installedStores.map((store) => (
                          <option key={store.shop} value={store.shop}>
                            {store.shop_name} ({store.shop})
                            {store.is_active_for_sync ? ' ✓ Active' : ''}
                          </option>
                        ))}
                      </select>
                      {switchingStore && (
                        <p className="text-sm text-gray-500 flex items-center">
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Switching store...
                        </p>
                      )}
                    </div>
                  )}

                  {/* No Stores Message with Instructions */}
                  {installedStores.length === 0 && !connectionStatus?.connected && (
                    <Alert className="bg-blue-50 border-blue-200">
                      <AlertCircle className="h-4 w-4 text-blue-600" />
                      <AlertDescription className="text-blue-800">
                        <p className="font-medium mb-2">No stores connected yet</p>
                        <p className="text-sm mb-2">If the Marketplace Connect App is already installed on your store:</p>
                        <ol className="list-decimal ml-4 text-sm space-y-1">
                          <li>Click "Add Store" above</li>
                          <li>Use the "Access Token" tab</li>
                          <li>Get your token from: Shopify Admin → Settings → Apps → Develop apps</li>
                        </ol>
                      </AlertDescription>
                    </Alert>
                  )}

                  {/* Installed Stores List */}
                  {installedStores.length > 0 ? (
                    <div className="space-y-3">
                      <Label className="text-sm font-medium">
                        Installed Stores ({installedStores.length})
                      </Label>
                      {installedStores.map((store) => (
                        <div 
                          key={store.shop}
                          className={`p-4 rounded-lg border ${
                            store.is_active_for_sync 
                              ? 'bg-green-50 border-green-200' 
                              : 'bg-gray-50 border-gray-200'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className={`p-2 rounded-full ${
                                store.is_active_for_sync ? 'bg-green-100' : 'bg-gray-100'
                              }`}>
                                {store.is_active_for_sync ? (
                                  <CheckCircle className="h-5 w-5 text-green-600" />
                                ) : (
                                  <Store className="h-5 w-5 text-gray-400" />
                                )}
                              </div>
                              <div>
                                <p className="font-medium text-gray-900">{store.shop_name}</p>
                                <p className="text-sm text-gray-500">{store.shop}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              {store.is_active_for_sync ? (
                                <Badge className="bg-green-100 text-green-700">Active</Badge>
                              ) : (
                                <Button 
                                  variant="outline" 
                                  size="sm"
                                  onClick={() => handleSwitchStore(store.shop)}
                                  disabled={switchingStore}
                                >
                                  Use This Store
                                </Button>
                              )}
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="sm">
                                    <MoreVertical className="h-4 w-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem onClick={() => window.open(`https://${store.shop}/admin`, '_blank')}>
                                    <ExternalLink className="h-4 w-4 mr-2" />
                                    Open Store Admin
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => window.open(`https://${store.shop}`, '_blank')}>
                                    <Globe className="h-4 w-4 mr-2" />
                                    View Storefront
                                  </DropdownMenuItem>
                                  {store.is_active_for_sync && (
                                    <>
                                      <DropdownMenuSeparator />
                                      <DropdownMenuItem onClick={() => handleTestConnection()}>
                                        <RefreshCw className="h-4 w-4 mr-2" />
                                        Test Connection
                                      </DropdownMenuItem>
                                      <DropdownMenuSeparator />
                                      <DropdownMenuItem onClick={() => handleReinstallScript()}>
                                        <Upload className="h-4 w-4 mr-2" />
                                        Reinstall Storefront Script
                                      </DropdownMenuItem>
                                      <DropdownMenuItem onClick={() => handleInjectToTheme()}>
                                        <Code className="h-4 w-4 mr-2" />
                                        Inject into Theme (OS 2.0)
                                      </DropdownMenuItem>
                                      <DropdownMenuItem onClick={() => handleCheckScriptStatus()}>
                                        <Activity className="h-4 w-4 mr-2" />
                                        Check Script Status
                                      </DropdownMenuItem>
                                    </>
                                  )}
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : connectionStatus?.connected ? (
                    <>
                      {/* Fallback: Show current connection if no app installations found */}
                      <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="p-2 bg-green-100 rounded-full">
                              <CheckCircle className="h-5 w-5 text-green-600" />
                            </div>
                            <div>
                              <p className="font-medium text-gray-900">{connectionStatus.shop_name}</p>
                              <p className="text-sm text-gray-500">{connectionStatus.shop_url}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge className="bg-green-100 text-green-700">Active</Badge>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm">
                                  <MoreVertical className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => window.open(`https://${connectionStatus.shop_url}/admin`, '_blank')}>
                                  <ExternalLink className="h-4 w-4 mr-2" />
                                  Open Store Admin
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => window.open(`https://${connectionStatus.shop_url}`, '_blank')}>
                                  <Globe className="h-4 w-4 mr-2" />
                                  View Storefront
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={() => handleTestConnection()}>
                                  <RefreshCw className="h-4 w-4 mr-2" />
                                  Test Connection
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={() => handleReinstallScript()}>
                                  <Upload className="h-4 w-4 mr-2" />
                                  Reinstall Storefront Script
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleInjectToTheme()}>
                                  <Code className="h-4 w-4 mr-2" />
                                  Inject into Theme (OS 2.0)
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleCheckScriptStatus()}>
                                  <Activity className="h-4 w-4 mr-2" />
                                  Check Script Status
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </div>
                      </div>

                      {/* Store Stats */}
                      <div className="grid grid-cols-3 gap-4 pt-4 border-t">
                        <div className="text-center p-3 bg-gray-50 rounded-lg">
                          <p className="text-2xl font-bold text-gray-900">{connectionStatus?.total_listings || 0}</p>
                          <p className="text-xs text-gray-500">Total Listings</p>
                        </div>
                        <div className="text-center p-3 bg-green-50 rounded-lg">
                          <p className="text-2xl font-bold text-green-600">{connectionStatus?.synced_listings || 0}</p>
                          <p className="text-xs text-gray-500">Synced</p>
                        </div>
                        <div className="text-center p-3 bg-amber-50 rounded-lg">
                          <p className="text-2xl font-bold text-amber-600">{connectionStatus?.pending_sync || 0}</p>
                          <p className="text-xs text-gray-500">Pending</p>
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="text-center py-8">
                      <CloudOff className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                      <p className="text-gray-600 font-medium">No Shopify Store Connected</p>
                      <p className="text-sm text-gray-500 mt-1 mb-4">
                        Install the Marketplace Connect app on your Shopify store
                      </p>
                      <Button onClick={() => setShowStoreModal(true)}>
                        <Store className="h-4 w-4 mr-2" />
                        Connect Shopify Store
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Add/Change Store Modal */}
              <Dialog open={showStoreModal} onOpenChange={setShowStoreModal}>
                <DialogContent className="max-w-lg">
                  <DialogHeader>
                    <DialogTitle>Connect Shopify Store</DialogTitle>
                    <DialogDescription>
                      Connect a store where Marketplace Connect App is installed
                    </DialogDescription>
                  </DialogHeader>
                  
                  <div className="space-y-4 py-4">
                    <div className="space-y-2">
                      <Label htmlFor="shop-url">Store URL</Label>
                      <div className="flex">
                        <Input
                          id="shop-url"
                          placeholder="your-store-name"
                          value={newStoreUrl}
                          onChange={(e) => setNewStoreUrl(e.target.value)}
                          className="rounded-r-none"
                        />
                        <span className="inline-flex items-center px-3 bg-gray-100 border border-l-0 border-gray-300 rounded-r-md text-sm text-gray-500">
                          .myshopify.com
                        </span>
                      </div>
                      <p className="text-xs text-gray-500">
                        Example: For "8gv6mv-0b.myshopify.com", enter "8gv6mv-0b"
                      </p>
                    </div>
                    
                    <Alert className="bg-blue-50 border-blue-200">
                      <AlertCircle className="h-4 w-4 text-blue-600" />
                      <AlertDescription className="text-blue-800 text-sm">
                        <p className="font-medium">How this works:</p>
                        <ul className="list-disc ml-4 mt-1 space-y-1">
                          <li>Uses Marketplace Connect App credentials (Client ID already configured)</li>
                          <li>Redirects to Shopify for authorization</li>
                          <li>If app is already installed, Shopify will quickly re-authorize</li>
                        </ul>
                      </AlertDescription>
                    </Alert>
                    
                    <Button 
                      className="w-full"
                      onClick={handleConnectNewStore}
                      disabled={!newStoreUrl || storeConnecting}
                    >
                      {storeConnecting ? (
                        <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Connecting...</>
                      ) : (
                        <><Store className="h-4 w-4 mr-2" />Connect Store via OAuth</>
                      )}
                    </Button>
                    
                    <div className="relative">
                      <div className="absolute inset-0 flex items-center">
                        <span className="w-full border-t" />
                      </div>
                      <div className="relative flex justify-center text-xs uppercase">
                        <span className="bg-white px-2 text-gray-500">Or use access token</span>
                      </div>
                    </div>
                    
                    <div className="space-y-3 p-4 bg-gray-50 rounded-lg">
                      <p className="text-sm text-gray-600">
                        If you have a Custom App access token:
                      </p>
                      <div className="space-y-2">
                        <Input
                          type="password"
                          placeholder="Access Token (shpat_xxxxx)"
                          value={accessToken}
                          onChange={(e) => setAccessToken(e.target.value)}
                        />
                        <Input
                          type="password"
                          placeholder="Webhook Signing Secret (optional, for HMAC validation)"
                          value={webhookSecret}
                          onChange={(e) => setWebhookSecret(e.target.value)}
                          data-testid="webhook-secret-input"
                        />
                        <p className="text-xs text-gray-400">
                          Find webhook secret in Shopify Admin → Settings → Notifications → Webhooks
                        </p>
                      </div>
                      <Button 
                        variant="outline"
                        className="w-full"
                        onClick={handleConnectWithToken}
                        disabled={!newStoreUrl || !accessToken || storeConnecting}
                      >
                        <CheckCircle className="h-4 w-4 mr-2" />
                        Connect with Token
                      </Button>
                    </div>
                  </div>
                  
                  <DialogFooter>
                    <Button variant="outline" onClick={() => {
                      setShowStoreModal(false);
                      setNewStoreUrl('');
                      setAccessToken('');
                      setWebhookSecret('');
                    }}>
                      Cancel
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>

              {/* Content Protection Card */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Shield className="h-5 w-5 text-blue-600" />
                    Content Protection
                  </CardTitle>
                  <CardDescription>
                    Protect product images and descriptions from being copied on your Shopify storefront
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Main Toggle */}
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border">
                    <div className="flex items-center gap-4">
                      <div className={`p-3 rounded-full ${contentProtection.enabled ? 'bg-green-100' : 'bg-gray-200'}`}>
                        {contentProtection.enabled ? (
                          <Lock className="h-6 w-6 text-green-600" />
                        ) : (
                          <Shield className="h-6 w-6 text-gray-400" />
                        )}
                      </div>
                      <div>
                        <Label htmlFor="content-protection" className="text-base font-semibold cursor-pointer">
                          Enable Content Protection
                        </Label>
                        <p className="text-sm text-gray-500 mt-0.5">
                          {contentProtection.enabled 
                            ? 'Protection is active on all connected Shopify stores' 
                            : 'Enable to protect product images and text from copying'}
                        </p>
                      </div>
                    </div>
                    <Switch
                      id="content-protection"
                      checked={contentProtection.enabled}
                      onCheckedChange={handleToggleContentProtection}
                      disabled={contentProtection.loading || !connectionStatus?.connected}
                    />
                  </div>

                  {/* Protection Features */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className={`p-4 rounded-lg border ${contentProtection.enabled ? 'bg-green-50 border-green-200' : 'bg-gray-50'}`}>
                      <div className="flex items-center gap-2 mb-2">
                        <Image className={`h-5 w-5 ${contentProtection.enabled ? 'text-green-600' : 'text-gray-400'}`} />
                        <span className="font-medium">Image Protection</span>
                      </div>
                      <p className="text-sm text-gray-600">
                        Blocks right-click and drag on product images
                      </p>
                      {contentProtection.enabled && (
                        <Badge className="mt-2 bg-green-100 text-green-700">Active</Badge>
                      )}
                    </div>
                    
                    <div className={`p-4 rounded-lg border ${contentProtection.enabled ? 'bg-green-50 border-green-200' : 'bg-gray-50'}`}>
                      <div className="flex items-center gap-2 mb-2">
                        <Lock className={`h-5 w-5 ${contentProtection.enabled ? 'text-green-600' : 'text-gray-400'}`} />
                        <span className="font-medium">Text Selection</span>
                      </div>
                      <p className="text-sm text-gray-600">
                        Prevents copying of product descriptions
                      </p>
                      {contentProtection.enabled && (
                        <Badge className="mt-2 bg-green-100 text-green-700">Active</Badge>
                      )}
                    </div>
                    
                    <div className={`p-4 rounded-lg border ${contentProtection.enabled ? 'bg-blue-50 border-blue-200' : 'bg-gray-50'}`}>
                      <div className="flex items-center gap-2 mb-2">
                        <CheckCircle className={`h-5 w-5 ${contentProtection.enabled ? 'text-blue-600' : 'text-gray-400'}`} />
                        <span className="font-medium">Checkout Safe</span>
                      </div>
                      <p className="text-sm text-gray-600">
                        Selection allowed on checkout pages for usability
                      </p>
                      {contentProtection.enabled && (
                        <Badge className="mt-2 bg-blue-100 text-blue-700">Smart Mode</Badge>
                      )}
                    </div>
                  </div>

                  {/* Status Info */}
                  {contentProtection.enabled && (
                    <Alert className="bg-green-50 border-green-200">
                      <Shield className="h-4 w-4 text-green-600" />
                      <AlertDescription className="text-green-800">
                        <strong>Content Protection is Active</strong>
                        <p className="text-sm mt-1">
                          A protection script has been injected into all connected Shopify storefronts. 
                          This script prevents casual copying of your product images and descriptions.
                        </p>
                        {contentProtection.lastSyncedAt && (
                          <p className="text-xs mt-2 text-green-600">
                            Last synced: {new Date(contentProtection.lastSyncedAt).toLocaleString()}
                          </p>
                        )}
                      </AlertDescription>
                    </Alert>
                  )}

                  {!connectionStatus?.connected && (
                    <Alert variant="destructive">
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>
                        Please connect to Shopify first to enable content protection.
                      </AlertDescription>
                    </Alert>
                  )}

                  {/* How It Works */}
                  <div className="border-t pt-4 mt-4">
                    <h4 className="font-medium text-gray-900 mb-3">How Content Protection Works</h4>
                    <div className="space-y-2 text-sm text-gray-600">
                      <p>• <strong>ScriptTag Injection:</strong> A lightweight JavaScript is added to your Shopify store via the ScriptTag API</p>
                      <p>• <strong>Cross-Store:</strong> Protection is applied to all vendor stores connected to the marketplace</p>
                      <p>• <strong>Non-Intrusive:</strong> Normal shopping experience is preserved - only copying actions are blocked</p>
                      <p>• <strong>Instant Toggle:</strong> Can be enabled or disabled at any time without affecting your products</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* ── Inventory Audit Modal ── */}

      {/* ── Liquid Audit Modal ── */}
      <Dialog open={showLiquidAuditModal} onOpenChange={setShowLiquidAuditModal}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Liquid Audit — PDP vs Quick View</DialogTitle>
            <DialogDescription>
              Compares Liquid rendering paths to find why <code>variant-json</code> / <code>main-product-options</code> are missing from the main PDP.
            </DialogDescription>
          </DialogHeader>
          {liquidAuditLoading ? (
            <div className="flex items-center justify-center h-40">
              <Loader2 className="h-8 w-8 animate-spin text-violet-500" />
              <span className="ml-3 text-gray-500">Reading theme files from Shopify…</span>
            </div>
          ) : liquidAuditReport ? (
            <div className="space-y-4 text-sm">
              {/* Theme Info */}
              <div className="bg-gray-50 border rounded p-3">
                <p className="font-medium text-gray-700">Theme: <span className="text-violet-700">{liquidAuditReport.theme_name}</span> (ID: {liquidAuditReport.theme_id})</p>
                <p className="text-gray-500 mt-1">Total assets: {liquidAuditReport.total_assets} | Sections/Snippets scanned: {liquidAuditReport.sections_snippets_count}</p>
              </div>

              {/* PDP Template */}
              <div className="border rounded p-3">
                <p className="font-semibold text-gray-700 mb-1">Main PDP Template: <code className="text-xs bg-gray-100 px-1 rounded">{liquidAuditReport.pdp_template_key}</code></p>
                {liquidAuditReport.pdp_section_types?.length > 0 && (
                  <p className="text-xs text-gray-500">Section types: {liquidAuditReport.pdp_section_types.join(', ')}</p>
                )}
                {liquidAuditReport.pdp_section_details && (
                  <div className="mt-2 space-y-2">
                    {Object.entries(liquidAuditReport.pdp_section_details).map(([id, sec]) => (
                      <div key={id} className={`rounded p-2 ${sec.has_variant_json && sec.has_main_product_options ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{id}</span>
                          <code className="text-xs bg-white px-1 rounded border">{sec.key}</code>
                        </div>
                        <div className="flex gap-3 mt-1 text-xs">
                          <span className={sec.has_variant_json ? 'text-green-700' : 'text-red-600 font-semibold'}>
                            {sec.has_variant_json ? '✓' : '✗'} variant-json
                          </span>
                          <span className={sec.has_main_product_options ? 'text-green-700' : 'text-red-600 font-semibold'}>
                            {sec.has_main_product_options ? '✓' : '✗'} main-product-options
                          </span>
                        </div>
                        {sec.blocks_defined_in_schema?.length > 0 && (
                          <p className="text-xs text-gray-400 mt-1">Schema blocks: {sec.blocks_defined_in_schema.join(', ')}</p>
                        )}
                        {sec.blocks_in_template?.length > 0 && (
                          <p className="text-xs text-gray-400">Template blocks active: {sec.blocks_in_template.join(', ')}</p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Quick View Template */}
              <div className="border rounded p-3">
                <p className="font-semibold text-gray-700 mb-1">Quick View Template: <code className="text-xs bg-gray-100 px-1 rounded">{liquidAuditReport.qv_template_key || 'not found'}</code></p>
                {liquidAuditReport.qv_section_types?.length > 0 && (
                  <p className="text-xs text-gray-500">Section types: {liquidAuditReport.qv_section_types.join(', ')}</p>
                )}
              </div>

              {/* JSON Block Locations */}
              <div className="border rounded p-3">
                <p className="font-semibold text-gray-700 mb-2">Files containing variant-json / main-product-options</p>
                {liquidAuditReport.json_block_locations?.length > 0 ? (
                  liquidAuditReport.json_block_locations.map((loc, i) => (
                    <div key={i} className="bg-green-50 border border-green-200 rounded p-2 mb-2">
                      <p className="font-medium text-green-800"><code className="text-xs">{loc.key}</code></p>
                      <div className="flex gap-3 mt-1 text-xs">
                        <span className={loc.has_variant_json ? 'text-green-700' : 'text-gray-400'}>{loc.has_variant_json ? '✓' : '–'} variant-json</span>
                        <span className={loc.has_main_product_options ? 'text-green-700' : 'text-gray-400'}>{loc.has_main_product_options ? '✓' : '–'} main-product-options</span>
                      </div>
                      {loc.variant_json_context && (
                        <pre className="text-xs text-gray-600 bg-white border rounded p-2 mt-2 overflow-x-auto max-h-28">{loc.variant_json_context}</pre>
                      )}
                    </div>
                  ))
                ) : (
                  <div className="bg-red-50 border border-red-200 rounded p-3 text-red-700 text-xs">
                    Neither JSON block found in any scanned section/snippet. The theme may render them inside a deeply nested snippet not yet fetched.
                  </div>
                )}
              </div>

              {/* Action */}
              <div className="bg-amber-50 border border-amber-200 rounded p-3">
                <p className="font-semibold text-amber-800 mb-1">Recommended Action</p>
                <p className="text-xs text-amber-700">
                  If the PDP section is missing the JSON blocks (shown in red above), click <strong>Patch: Inject variant-json Blocks</strong> to add them unconditionally to the main product section.
                </p>
                <Button
                  className="mt-2 bg-orange-600 hover:bg-orange-700 text-white text-xs h-8"
                  onClick={() => { setShowLiquidAuditModal(false); handlePatchVariantJson(); }}
                  disabled={patchingVariantJson}
                  data-testid="liquid-audit-patch-btn"
                >
                  {patchingVariantJson ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : null}
                  Patch: Inject variant-json Blocks
                </Button>
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>


      {/* ── Render Chain RCA Modal ── */}
      <Dialog open={showRenderChainModal} onOpenChange={setShowRenderChainModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Render Chain Root-Cause Analysis</DialogTitle>
            <DialogDescription>
              Traces every Liquid render call from <code>templates/product.json</code> to <code>snippets/product-variants.liquid</code> for both the main PDP and Quick View.
            </DialogDescription>
          </DialogHeader>

          {renderChainLoading ? (
            <div className="flex items-center justify-center h-40">
              <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
              <span className="ml-3 text-gray-500">Reading theme render chains from Shopify…</span>
            </div>
          ) : renderChainReport ? (
            <div className="space-y-4 text-sm font-mono">

              {/* Theme */}
              <div className="bg-gray-50 border rounded p-3 font-sans">
                <p className="font-semibold">Theme: <span className="text-blue-700">{renderChainReport.theme_name}</span> (ID: {renderChainReport.theme_id})</p>
                <p className="text-xs text-gray-500 mt-1">Main PDP section type: <code>{renderChainReport.pdp_main_section_type || 'not found'}</code></p>
              </div>

              {/* Diagnosis */}
              {renderChainReport.diagnosis?.map((d, i) => (
                <div key={i} className={`rounded p-3 border font-sans ${d.startsWith('ROOT CAUSE') ? 'bg-red-50 border-red-300 text-red-800' : 'bg-blue-50 border-blue-200 text-blue-800'}`}>
                  <p className="font-semibold text-xs uppercase tracking-wide mb-1">{d.startsWith('ROOT') ? 'Root Cause' : 'Finding'}</p>
                  <p className="text-sm leading-relaxed">{d}</p>
                </div>
              ))}

              {/* Recommended Fix */}
              {renderChainReport.recommended_fix && (
                <div className="bg-green-50 border border-green-300 rounded p-3 font-sans">
                  <p className="font-semibold text-xs uppercase tracking-wide text-green-800 mb-1">Recommended Fix</p>
                  <p className="text-sm text-green-900 leading-relaxed">{renderChainReport.recommended_fix}</p>
                </div>
              )}

              {/* PDP Block ID → Type map */}
              {renderChainReport.pdp_block_id_to_type && Object.keys(renderChainReport.pdp_block_id_to_type).length > 0 && (
                <details className="border rounded">
                  <summary className="p-2 cursor-pointer font-sans font-semibold text-gray-700 bg-gray-50">
                    PDP Active Block Map ({Object.keys(renderChainReport.pdp_block_id_to_type).length} blocks)
                    {Object.keys(renderChainReport.pdp_variant_blocks_active || {}).length > 0
                      ? <span className="ml-2 text-green-700 font-normal">✓ variant block present</span>
                      : <span className="ml-2 text-red-600 font-normal font-semibold">✗ NO variant block!</span>
                    }
                  </summary>
                  <div className="p-3 space-y-1 text-xs max-h-48 overflow-y-auto">
                    {Object.entries(renderChainReport.pdp_block_id_to_type).map(([id, type]) => (
                      <div key={id} className={`flex gap-2 ${['variant','product_variations','variant_picker'].some(x=>type.includes(x)) ? 'text-green-700 font-semibold' : 'text-gray-600'}`}>
                        <span className="truncate max-w-xs">{id}</span>
                        <span>→</span>
                        <span>{type}</span>
                      </div>
                    ))}
                  </div>
                </details>
              )}

              {/* PDP Section Render Chain */}
              <details className="border rounded" open>
                <summary className="p-2 cursor-pointer font-sans font-semibold text-gray-700 bg-gray-50">
                  PDP Render Chain: sections/{renderChainReport.pdp_main_section_type}.liquid
                </summary>
                <div className="p-3 text-xs space-y-1">
                  <p className="font-sans text-gray-500 mb-2">Top-level render calls:</p>
                  {(renderChainReport.pdp_section_renders || []).map((r, i) => (
                    <div key={i} className={`p-1 rounded ${r === 'product-variants' ? 'bg-green-100 text-green-800 font-semibold' : 'text-gray-700'}`}>
                      {`{% render '${r}' %}`}
                    </div>
                  ))}
                  {(renderChainReport.pdp_section_renders || []).length === 0 && (
                    <p className="text-gray-400 italic">No direct top-level render calls</p>
                  )}
                  <p className="font-sans text-gray-500 mt-3 mb-1">Render calls per active block type:</p>
                  {Object.entries(renderChainReport.pdp_block_type_render_chains || {}).map(([type, renders]) => (
                    <div key={type} className="mb-1">
                      <span className={`font-semibold ${['variant','product_variations','variant_picker'].some(x=>type.includes(x)) ? 'text-blue-700' : 'text-gray-600'}`}>{type}:</span>
                      {' '}{renders.join(', ') || 'none'}
                    </div>
                  ))}
                </div>
              </details>

              {/* QV Render Chain */}
              <details className="border rounded" open>
                <summary className="p-2 cursor-pointer font-sans font-semibold text-gray-700 bg-gray-50">
                  Quick View Render Chain: {renderChainReport.qv_template_key || 'not found'}
                </summary>
                <div className="p-3 text-xs space-y-1">
                  {(renderChainReport.qv_renders || []).map((r, i) => {
                    const chain = renderChainReport.qv_snippet_chain?.[r];
                    return (
                      <div key={i} className={`p-1 rounded ${chain?.has_variant_json ? 'bg-green-100 text-green-800 font-semibold' : 'text-gray-700'}`}>
                        {`{% render '${r}' %}`}
                        {chain?.has_variant_json && ' ← contains variant-json'}
                        {chain?.qv_render_call && <span className="text-gray-400 ml-2">{chain.qv_render_call}</span>}
                      </div>
                    );
                  })}
                </div>
              </details>

              {/* Snippet Chain Detail */}
              <details className="border rounded">
                <summary className="p-2 cursor-pointer font-sans font-semibold text-gray-700 bg-gray-50">Snippet Details</summary>
                <div className="p-3 space-y-3 text-xs">
                  {Object.entries({...(renderChainReport.pdp_snippet_chain||{}), ...(renderChainReport.qv_snippet_chain||{})}).map(([name, info]) => (
                    <div key={name} className={`border rounded p-2 ${info.has_variant_json ? 'border-green-300 bg-green-50' : 'border-gray-200'}`}>
                      <p className="font-semibold font-sans">{name}.liquid {info.found ? '' : <span className="text-red-500">(NOT FOUND)</span>}</p>
                      {info.found && (
                        <>
                          <div className="flex gap-3 mt-1">
                            <span className={info.has_variant_json ? 'text-green-700' : 'text-gray-400'}>{info.has_variant_json ? '✓' : '–'} variant-json</span>
                            <span className={info.has_main_product_options ? 'text-green-700' : 'text-gray-400'}>{info.has_main_product_options ? '✓' : '–'} main-product-options</span>
                          </div>
                          {info.sub_renders?.length > 0 && <p className="text-gray-500 mt-1">renders: {info.sub_renders.join(', ')}</p>}
                          {info.variant_json_context && (
                            <pre className="mt-2 bg-white border rounded p-1 overflow-x-auto max-h-20 text-gray-600">{info.variant_json_context}</pre>
                          )}
                        </>
                      )}
                    </div>
                  ))}
                </div>
              </details>

              {/* ── File Inspector ── */}
              <div className="border-2 border-blue-300 rounded p-3 bg-blue-50 font-sans">
                <p className="font-semibold text-blue-800 mb-2 text-xs uppercase tracking-wide">Inspect a Theme File (Read-Only)</p>
                <div className="flex gap-2 mb-2">
                  <input
                    className="flex-1 border border-blue-300 rounded px-2 py-1 text-xs font-mono bg-white"
                    placeholder="snippets/product-form.liquid"
                    value={inspectKey}
                    onChange={e => setInspectKey(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleInspectFile()}
                    data-testid="inspect-file-input"
                  />
                  <Button
                    size="sm"
                    className="bg-blue-700 hover:bg-blue-800 text-white text-xs px-3"
                    onClick={() => handleInspectFile()}
                    disabled={inspectLoading || !inspectKey}
                    data-testid="inspect-file-btn"
                  >
                    {inspectLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Read'}
                  </Button>
                </div>
                {/* Quick access buttons for the two key files */}
                <div className="flex flex-wrap gap-1 mb-2">
                  {['snippets/product-form.liquid','snippets/product-variations.liquid','snippets/product-variants.liquid','templates/product.quick-view.liquid'].map(f => (
                    <button
                      key={f}
                      className="text-xs bg-white border border-blue-200 rounded px-2 py-0.5 hover:bg-blue-100 font-mono text-blue-700"
                      onClick={() => { setInspectKey(f); handleInspectFile(f); }}
                    >{f.split('/').pop()}</button>
                  ))}
                </div>
                {inspectedFile && (
                  <div className="mt-2">
                    <p className="font-semibold text-xs text-blue-800 mb-1">
                      <code>{inspectedFile.key}</code> — {inspectedFile.line_count} lines
                      {inspectedFile.has_product_variants_call
                        ? <span className="ml-2 text-green-700">✓ calls product-variants</span>
                        : <span className="ml-2 text-red-600 font-semibold">✗ does NOT call product-variants</span>
                      }
                    </p>
                    {/* product-variants occurrences with context */}
                    {inspectedFile.product_variants_context?.length > 0 && (
                      <div className="mb-2">
                        <p className="text-xs text-gray-500 mb-1">product-variants occurrence context:</p>
                        {inspectedFile.product_variants_context.map((ctx, i) => (
                          <pre key={i} className="text-xs bg-white border border-green-300 rounded p-2 overflow-x-auto text-green-800 mb-1">{ctx.context}</pre>
                        ))}
                      </div>
                    )}
                    {/* All render calls */}
                    <details>
                      <summary className="text-xs text-gray-500 cursor-pointer mb-1">All render/include calls ({inspectedFile.all_render_calls?.length})</summary>
                      <div className="space-y-0.5 max-h-40 overflow-y-auto">
                        {inspectedFile.all_render_calls?.map((rc, i) => (
                          <div key={i} className={`text-xs font-mono p-0.5 rounded ${rc.snippet === 'product-variants' ? 'bg-green-100 text-green-800 font-semibold' : 'text-gray-600'}`}>
                            <span className="text-gray-400 mr-1">{rc.line}:</span>{rc.full_line}
                          </div>
                        ))}
                      </div>
                    </details>
                    {/* Full content */}
                    <details className="mt-2">
                      <summary className="text-xs text-gray-500 cursor-pointer">Full file content</summary>
                      <pre className="mt-1 text-xs bg-white border rounded p-2 overflow-x-auto max-h-96 text-gray-700 whitespace-pre-wrap">{inspectedFile.full_content}</pre>
                    </details>
                  </div>
                )}
              </div>

              {/* Raw JSON toggle */}
              <details className="border rounded">
                <summary className="p-2 cursor-pointer font-sans text-xs text-gray-400">Raw JSON report</summary>
                <pre className="p-3 text-xs overflow-x-auto max-h-60 bg-gray-50">{JSON.stringify(renderChainReport, null, 2)}</pre>
              </details>

            </div>
          ) : null}
        </DialogContent>
      </Dialog>



      <Dialog open={showAuditModal} onOpenChange={setShowAuditModal}>
        <DialogContent className="max-w-5xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Inventory Audit — Portal vs Shopify</DialogTitle>
            <DialogDescription>
              Live comparison of inventory in Seller Portal (SSOT) against what&apos;s currently on Shopify.
            </DialogDescription>
          </DialogHeader>

          {auditLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-6 w-6 animate-spin text-teal-500" />
              <span className="ml-3 text-sm text-gray-500">Fetching inventory from Shopify API...</span>
            </div>
          ) : auditData ? (
            <div className="space-y-4">
              {/* Summary Bar */}
              <div className="grid grid-cols-4 gap-3 text-center">
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-2xl font-bold">{auditData.total}</p>
                  <p className="text-xs text-gray-500">Total Audited</p>
                </div>
                <div className="bg-green-50 rounded-lg p-3">
                  <p className="text-2xl font-bold text-green-700">{auditData.matched}</p>
                  <p className="text-xs text-green-600">Matched</p>
                </div>
                <div className="bg-red-50 rounded-lg p-3">
                  <p className="text-2xl font-bold text-red-700">{auditData.mismatched}</p>
                  <p className="text-xs text-red-600">Mismatched</p>
                </div>
                <div className="bg-amber-50 rounded-lg p-3">
                  <p className="text-2xl font-bold text-amber-700">{auditData.errors}</p>
                  <p className="text-xs text-amber-600">Errors / Not Found</p>
                </div>
              </div>

              {/* Results Table */}
              <div className="border rounded-lg overflow-hidden">
                <div className="grid grid-cols-[2.5fr_1fr_1fr_0.8fr] gap-2 px-4 py-2 bg-gray-50 text-xs font-medium text-gray-500 border-b">
                  <span>Product</span>
                  <span className="text-right">Portal Stock</span>
                  <span className="text-right">Shopify Stock</span>
                  <span className="text-center">Status</span>
                </div>
                <div className="max-h-[400px] overflow-y-auto divide-y">
                  {auditData.results?.map((item) => (
                    <div key={item.listing_id} className={`grid grid-cols-[2.5fr_1fr_1fr_0.8fr] gap-2 px-4 py-2.5 text-sm items-center ${
                      item.status === 'mismatch' ? 'bg-red-50/50' : item.status === 'not_found' ? 'bg-amber-50/50' : ''
                    }`}>
                      <div className="truncate">
                        <span className="font-medium" title={item.title}>{item.title}</span>
                        {item.has_variants && <span className="ml-1 text-xs text-gray-400">(variants)</span>}
                      </div>
                      <span className="text-right font-mono tabular-nums">{item.portal_inventory}</span>
                      <span className={`text-right font-mono tabular-nums ${
                        item.shopify_inventory === -1 ? 'text-amber-600' : 
                        item.status === 'mismatch' ? 'text-red-600 font-semibold' : ''
                      }`}>
                        {item.shopify_inventory === -1 ? 'N/A' : item.shopify_inventory}
                      </span>
                      <div className="flex justify-center">
                        {item.status === 'match' ? (
                          <span className="inline-flex items-center gap-1 text-xs text-green-700 bg-green-100 px-2 py-0.5 rounded-full">
                            <CheckCircle className="h-3 w-3" /> Match
                          </span>
                        ) : item.status === 'mismatch' ? (
                          <span className="inline-flex items-center gap-1 text-xs text-red-700 bg-red-100 px-2 py-0.5 rounded-full">
                            <XCircle className="h-3 w-3" /> Drift
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-xs text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full">
                            <AlertTriangle className="h-3 w-3" /> Lost
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {auditData.mismatched > 0 && (
                <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
                  <RefreshCw className="h-4 w-4 shrink-0" />
                  <span>Run <strong>"Sync All Inventory"</strong> to push corrected Portal values to Shopify for all mismatched products.</span>
                </div>
              )}
            </div>
          ) : null}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAuditModal(false)}>Close</Button>
            {auditData?.mismatched > 0 && (
              <Button onClick={() => { setShowAuditModal(false); handleSyncAllInventory(); }}>
                <RefreshCw className="h-4 w-4 mr-2" /> Sync All Inventory Now
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Fresh Push All Confirmation Modal ── */}
      <Dialog open={showFreshPushModal} onOpenChange={setShowFreshPushModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-700 flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" />
              Fresh Push All — Destructive Action
            </DialogTitle>
            <DialogDescription asChild>
              <div className="text-sm space-y-3 pt-2">
                <span className="block">This will <strong>disconnect ALL listings</strong> from their current Shopify products and reset them to pending.</span>
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-800 text-xs space-y-1">
                  <span className="block font-semibold">Before proceeding, you MUST:</span>
                  <span className="block">1. Go to Shopify Admin → Products → Select All → Bulk Delete</span>
                  <span className="block">2. This clears the slate on Shopify to avoid duplicates</span>
                  <span className="block">3. Then run "Sync All Pending" after this action</span>
                </div>
                <span className="block text-xs text-gray-500">Type <code className="bg-gray-100 px-1.5 py-0.5 rounded font-mono text-red-600">FRESH PUSH</code> to confirm:</span>
              </div>
            </DialogDescription>
          </DialogHeader>
          <Input
            placeholder='Type "FRESH PUSH" to confirm'
            value={freshPushConfirmText}
            onChange={(e) => setFreshPushConfirmText(e.target.value)}
            className="font-mono text-center"
            data-testid="fresh-push-confirm-input"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowFreshPushModal(false)}>Cancel</Button>
            <Button
              variant="destructive"
              onClick={handleFreshPushAll}
              disabled={freshPushRunning || freshPushConfirmText !== 'FRESH PUSH'}
              data-testid="confirm-fresh-push-btn"
            >
              {freshPushRunning ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
              Disconnect & Reset All
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Remap Shopify IDs Modal ── */}
      <Dialog open={showRemapModal} onOpenChange={setShowRemapModal}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Remap Shopify Product IDs</DialogTitle>
            <DialogDescription>
              Paste the Shopify admin URL or product ID for each failed listing. 
              Example: <code className="text-xs bg-gray-100 px-1 py-0.5 rounded">https://admin.shopify.com/store/yourstore/products/8532184268935</code>
            </DialogDescription>
          </DialogHeader>

          {remapLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
              <span className="ml-2 text-sm text-gray-500">Loading failed listings...</span>
            </div>
          ) : remapListings.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <CheckCircle className="h-8 w-8 mx-auto mb-2 text-green-500" />
              <p>No failed listings to remap.</p>
            </div>
          ) : (
            <div className="space-y-1">
              <div className="grid grid-cols-[2fr_1fr_2fr] gap-3 text-xs font-medium text-gray-500 px-1 pb-1 border-b">
                <span>Listing</span>
                <span>Current Shopify ID</span>
                <span>New Shopify URL / ID</span>
              </div>
              {remapListings.map((item) => (
                <div key={item.listing_id} className="grid grid-cols-[2fr_1fr_2fr] gap-3 items-center py-2 border-b border-gray-50">
                  <div>
                    <p className="text-sm font-medium truncate" title={item.title}>{item.title}</p>
                    <p className="text-xs text-red-500 truncate" title={item.error}>{item.error?.substring(0, 80)}</p>
                  </div>
                  <div>
                    <code className="text-xs text-gray-500">{item.current_shopify_id || '—'}</code>
                  </div>
                  <div>
                    <Input
                      placeholder="Paste Shopify URL or product ID"
                      className="text-xs h-8"
                      value={remapInputs[item.listing_id] || ''}
                      onChange={(e) => handleRemapInputChange(item.listing_id, e.target.value)}
                      data-testid={`remap-input-${item.listing_id}`}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowRemapModal(false)}>Cancel</Button>
            <Button
              onClick={handleBulkRemap}
              disabled={remapSaving || Object.values(remapInputs).filter(v => v?.trim()).length === 0}
              data-testid="save-remap-btn"
            >
              {remapSaving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Save Mappings ({Object.values(remapInputs).filter(v => v?.trim()).length})
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </DashboardLayout>
  );
};

export default AdminShopifySync;
